<template>
  <div style="width: 100%;" v-loading.body="loading" element-loading-text="Loading..." class="gl-reporter">
    <b-navbar v-show="isWorksheet">
      <b-navbar-nav small>
        <b-nav-item @click="changeTab('option')" :active="tab === 'option'">
          <b-icon icon="paperclip" class="bicon"></b-icon>General Ledger Worksheet Options
        </b-nav-item>
        <b-nav-item @click="changeTab('report')" :active="tab === 'report'" :disabled="isLoading">
          <b-icon icon="table" class="bicon tbl"></b-icon>Worksheet
        </b-nav-item>
        <b-nav-item @click="changeTab('viewer')" :active="tab === 'viewer'" :disabled="!isPrinted">
          <b-icon icon="pie-chart" class="bicon"></b-icon>Viewer
        </b-nav-item>
      </b-navbar-nav>

      <b-navbar-nav v-show="tab === 'report'" class="ml-auto mr-2" small>
        <b-nav-item @click="toggleBulk" link-classes="nav-blue" v-if="selection.length">
          <b-icon icon="collection" class="bicon white"></b-icon>Bulk Update
        </b-nav-item>
        <b-nav-item :disabled="true" v-else>
          <b-icon icon="collection" class="bicon" />Bulk Update
        </b-nav-item>
        <template v-if="isGroupedByAccount">
          <b-nav-item @click="toggleExpand" link-classes="nav-blue" v-if="displayOptions.groupingLevel === 1">
            <b-icon icon="arrows-expand" class="bicon white"></b-icon>Expand All
          </b-nav-item>
          <b-nav-item @click="toggleCollapse" link-classes="nav-red" v-else>
            <b-icon icon="arrows-collapse" class="bicon white"></b-icon>Collapse All
          </b-nav-item>
        </template>
        <b-nav-item @click="buildWorkSheet(true)" link-classes="bold" :disabled="isLoading || isSaving || isPrinting">
          <b-icon icon="arrow-repeat" class="bicon"></b-icon>Reset / Refresh
        </b-nav-item>
        <b-nav-item-dropdown class="mb-2" toggle-class="active" offset="0" :disabled="isLoading" left>
            <template slot="button-content"><b-icon icon="bullseye" class="bicon"></b-icon>Options</template>
            <b-dropdown-form>
              <b-form-checkbox v-model="displayOptions.hideDecimals">Hide Decimals</b-form-checkbox>
              <b-form-checkbox v-model="displayOptions.groupByAccount" @change="onToggleGroupByAccount">Group by Account</b-form-checkbox>
            </b-dropdown-form>
            <b-dropdown-form>
                Show/Hide Columns
                <b-form-checkbox v-for="column in displayOptions.columns" v-model="column.display" @change="onChangeVisibleColumns(column.name)" :key="column.name">
                  {{ column.displayName }}
                </b-form-checkbox>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click="onExport" :disabled="isLoading || isSaving || isPrinting">
                  Export to Excel
                </b-dropdown-item>
            </b-dropdown-form>
        </b-nav-item-dropdown>
        <b-nav-item @click="saveChanges" link-classes="nav-green" :disabled="isSaving" v-if="allowEdit">
          <b-icon icon="calendar-fill" class="bicon white save"></b-icon>
          <span v-if="isSaving">
            <b-spinner class="mr-2" small />Saving
          </span>
          <span v-else>
            Save
          </span>
        </b-nav-item>
        <b-nav-item v-else>
          <b-icon icon="lock" class="bicon"></b-icon> Read Only
        </b-nav-item>
        <b-nav-item-dropdown class="nav-blue mb-1 mr-1" toggle-class="active print" offset="0" :disabled="isLoading" right>
          <template slot="button-content">
            <b-icon v-if="!isPrinting" icon="printer-fill" class="bicon white"></b-icon>
            <span v-if="isPrinting">
              <b-spinner class="mr-2" small /> Printing
            </span>
            <span v-else>
              Print
            </span>
          </template>
          <b-dropdown-form>
            <b-dropdown-item @click="printPdf('detail')">
              Detail Report
            </b-dropdown-item>
            <b-dropdown-item @click="printPdf('summary')">
              Summary Report
            </b-dropdown-item>
          </b-dropdown-form>
        </b-nav-item-dropdown>
      </b-navbar-nav>
    </b-navbar>

    <b-container v-show="tab === 'option'" fluid id="options" ref="options">
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.5em;">
            GENERAL LEDGER REPORTING
          </el-row>
        </el-col>
        <el-col>
          <el-row type="flex" align="middle" justify="end">
            <el-button type="primary" @click="buildWorkSheet(false)" v-if="!disableReportRun" :disabled="!!transactionNumbersError">
              Build Worksheet
            </el-button>
            <el-button v-if="disableReportRun">
              <b-spinner class="mr-2" small /> Please Wait
            </el-button>
            <el-button plain @click="generateReport" :disabled="disableReportRun || !!transactionNumbersError" v-else>
              Run report
            </el-button>
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Saved Report Options
        </el-col>
        <el-col :span="4">
          <el-select v-model="selectedOption" :filterable="true" @change="handleSelection" data-fieldtype="select" :multiple="false" clearable placeholder="Search">
              <el-option
                v-for="opt in savedReportOptions"
                :key="opt.id"
                :label="opt.report_name"
                :value="opt.id">
              </el-option>
            </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Report Options
        </el-col>
        <el-col :span="20">
          <el-button @click="openSaveOption">Save Report Options</el-button>
          <el-button @click="loadOptions">Load Report Options</el-button>
          <el-button @click="deleteOptions">Delete Report Options</el-button>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Report Options
          </el-row>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Summary/Detail
        </el-col>
        <el-col :span="4">
          <el-radio-group v-model="reportOptions.type" @change="updateReportType">
            <el-radio-button label="detail">Detail</el-radio-button>
            <el-radio-button label="summary">Summary</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Periods/Dates
        </el-col>
        <el-col :span="21">
          <el-radio-group v-model="reportOptions.dates" @change="updateReportDates">
            <el-radio-button label="datesAll">All</el-radio-button>
            <el-radio-button label="datesPeriod">Period</el-radio-button>
            <el-radio-button label="datesPosted">Posting Date</el-radio-button>
            <el-radio-button label="datesEffective">Effective Date</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>

      <div v-if="reportOptions.dates==='datesPeriod'" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">Starting Period</el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">Ending Period</el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.period_start" :multiple="false" placeholder="First Period">
              <el-option
                v-for="opt in allCodes.periods"
                :key="opt.id"
                :label="opt.id + ' - ' + opt.start"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.period_end" :multiple="false" placeholder="Last Period">
              <el-option
                v-for="opt in allCodes.periods"
                :key="opt.id"
                :label="opt.id + ' - ' + opt.start"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
        </el-row>
      </div>

      <div v-if="reportOptions.dates==='datesPosted'" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">Posting Date Start</el-col>
          <el-col :span="3" />
          <el-col :span="4">Posting Date End</el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.time_posted_start" :key="reRender" />
          </el-col>
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.time_posted_end" :key="reRender" />
          </el-col>
        </el-row>
      </div>

      <div v-if="reportOptions.dates==='datesEffective'" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">Effective Date Start <sup>**</sup></el-col>
          <el-col :span="3" />
          <el-col :span="4">Effective Date End <sup>**</sup></el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.effective_date_start" :key="reRender"/>
          </el-col>
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.effective_date_end" :key="reRender"/>
          </el-col>
        </el-row>
        <el-row v-if="reportOptions.dates==='datesEffective'" type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="21"><p><sup>**</sup> Posting Date is used if Effective Date is not Set</p></el-col>
        </el-row>
      </div>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Company
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.company_id"
            :multiple="false" placeholder="All">
            <el-option
              v-for="opt in allCodes.companies"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Production
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.production_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.productions"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="selectRow">
        <el-col :span="3" class="right">
          Series
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.series_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.series"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Location
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.location_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.locations"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="selectRow">
        <el-col :span="3" class="right">
          Set
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.set_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.sets"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Free Field
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.free_field_1_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.freefield1"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Source Code
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.source_code_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.sourcecodes"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Assets
        </el-col>
        <el-col :span="4">
          <el-select v-model="selectedCodes.asset" placeholder="All">
            <el-option value="true" label="Show Only Assets"></el-option>
            <el-option value="false" label="Hide Assets"></el-option>
            <el-option :value="undefined">All Transactions</el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow" v-show="currencies.length > 1">
        <el-col :span="3" class="right">
          Currency
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.currency_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="cur in currencies"
              :key="cur.id"
              :label="cur.code + ' - ' + parseFloat(cur.rate_vs_usd || 0).toFixed(4)"
              :value="cur.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Convert To
        </el-col>
        <el-col :span="4">
          <el-select v-model="reportOptions.report_currency_id">
            <el-option
              v-for="opt in currencies"
              :key="opt.id"
              :label="opt.code + ' - ' + parseFloat(opt.rate_vs_usd || 0).toFixed(4)"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Report Output
        </el-col>
        <el-col :span="4">
          <el-select v-model="reportOptions.format">
            <el-option
              v-for="opt in reportOutputFormats"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value">
            </el-option>
          </el-select>
        </el-col>      
        <el-col :span="3" class="right" v-if="showUnposted">
          Include
        </el-col>
        <el-col :span="4" class="m-top-10" v-if="showUnposted">
          <el-checkbox v-model="reportOptions.includeunposted"></el-checkbox> Unposted Items
        </el-col>
      </el-row>
      <el-row v-if="reportOptions.format === 'csv'" type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Subtotal by Account
        </el-col>
        <el-col :span="4" class="m-top-10">
          <el-checkbox v-model="csvSubTotalByAccount" /> 
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow"> 
        <el-col :span="3" class="right">
          Include
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.pos.include"></el-checkbox> POs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.aps.include"></el-checkbox> APs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.jes.include"></el-checkbox> JEs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.pcs.include"></el-checkbox> PCs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.prs.include"></el-checkbox> PRs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.cks.include"></el-checkbox> CKs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.wts.include"></el-checkbox> WTs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.mcs.include"></el-checkbox> MCs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.efts.include"></el-checkbox> EFTs
        </el-col>
        <el-col :span="1" class="m-top-10" style="width: 5% !important">
          <el-checkbox v-model="transactionTypes.achs.include"></el-checkbox> ACHs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.dds.include"></el-checkbox> DDs
        </el-col>
        <el-col :span="2" class="m-top-10">
          <el-checkbox v-model="transactionTypes.nas.include"></el-checkbox> N/A
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right"> Summarize Lines </el-col>
        <el-col :span="2" class="m-top-10">
          <el-checkbox v-model="reportOptions.summarizeLines" :disabled="!accounts.some(acct => acct.is_summarized)"></el-checkbox>
        </el-col>
        <el-col :span="6" class="left">
          <div class="nav-wrapper acc"><router-link to="/accounts" target="_blank">Click here to go to Accounts COA</router-link></div>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3"></el-col>
        <el-col :span="3">
          <el-radio :label="one_line" v-model="reportOptions.summaryType" v-show="reportOptions.summarizeLines">One Line</el-radio>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="end" class="section">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Vendor Selection
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="24" :offset="1">
          <el-radio-group v-model="reportOptions.vendorQuickPick" @change="updateVendors" :disabled="loading || disableReportRun">
            <el-radio-button label="custom">Custom</el-radio-button>
            <el-radio-button label="all">All</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>
      <el-row v-show="reportOptions.vendorQuickPick==='custom'" type="flex" align="top" justify="start" class="headerRow" v-if="vendors && vendors.length > 0">
        <el-col :span="24" :offset="1">
          <el-transfer
            ref="vendorTransfer"
            v-model="selectedCodes.vendor_id"
            :data="vendors"
            :titles="[ 'Unselected Vendors', 'Selected Vendors' ]"
            :props="{ key: 'id', label: 'display_name' }"
            filterable
            filter-placeholder="Search Vendors"
          >
          </el-transfer>
        </el-col>
      </el-row>
      <div class="clear"></div>
      <el-row type="flex" align="middle" justify="end" class="section">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Account Selection
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="24" :offset="1">
          <el-radio-group v-model="reportOptions.accountQuickPick" @change="updateAccounts" :disabled="loading || disableReportRun">
            <el-radio-button label="custom">Custom</el-radio-button>
            <el-radio-button label="range">Range</el-radio-button>
            <el-radio-button label="all">All</el-radio-button>
            <el-radio-button label="expenses">Expenses Only</el-radio-button>
            <el-radio-button label="balance">Balance Sheet Only</el-radio-button>
            <el-radio-button label="none">None</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>
      <div v-if="reportOptions.accountQuickPick==='range' && allCodes.accounts && allCodes.accounts.length > 0" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">From</el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">To</el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.account_id2" clearable :multiple="false" placeholder="First Account" :filterable="true" @change="checkCodeValidity">
              <el-option
                v-for="opt in allCodes.accounts"
                :key="opt.id"
                :label="opt.code + ' - ' + opt.name"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.account_id1" clearable :multiple="false" placeholder="Last Account" :filterable="true" @change="checkCodeValidity">
              <el-option
                v-for="opt in filteredOptions2"
                :key="opt.id"
                :label="opt.code + ' - ' + opt.name"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
        </el-row>
      </div>

      <el-row v-show="reportOptions.accountQuickPick==='custom'" type="flex" align="top" justify="start" class="headerRow" v-if="allCodes.accounts && allCodes.accounts.length > 0">
        <el-col :span="24" :offset="1">
          <el-transfer
            ref="accountTransfer"
            v-model="selectedCodes.account_id"
            :data="accounts"
            :titles="[ 'Unselected Accounts', 'Selected Accounts' ]"
            :props="{ key: 'id', label: 'display_name' }"
            filterable
            filter-placeholder="Search Accounts"
          >
          </el-transfer>
        </el-col>
      </el-row>
      <div class="clear"></div>
      <el-row type="flex" align="middle" justify="end" class="section">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Transaction Selection
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="top" justify="start" class="headerRow">
        <el-col :span="10" :offset="1">
          <el-input
            v-model="selectedCodes.transaction_numbers"
            clearable
            :disabled="loading || disableReportRun"
            placeholder="e.g. 1, 2, 5-10, 12, 15-16">
            <template slot="prepend">Transaction Numbers</template>
          </el-input>
          <el-row v-if="transactionNumbersError" type="flex" class="m-top-10" style="color: #f56c6c; font-size: 0.85em;">
            {{ transactionNumbersError }}
          </el-row>
          <el-row v-else type="flex" class="m-top-10" style="color: #909399; font-size: 0.85em;">
            Leave blank for all transactions. Use commas to list individual numbers and a dash for a range.
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-button v-if="disableReportRun">
          <b-spinner class="mr-2" small /> Please Wait
        </el-button>
        <el-button plain @click="generateReport" :disabled="disableReportRun || !!transactionNumbersError" v-else>
          Run report
        </el-button>
      </el-row>
    </b-container>
    <b-container v-show="tab === 'report'" fluid>
      <ag-grid-vue
        v-if="gridVisible"
        name="worksheet"
        ref="worksheet"
        class="ag-theme-balham"
        :style="{ width, height }"
        :key="iteration"
        :column-defs="columnDefs"
        :grid-options="gridOptions"
        :auto-group-column-def="autoGroupColumnDef"
        :isExternalFilterPresent="isExternalFilterPresent"
        :doesExternalFilterPass="doesExternalFilterPass"
        @row-selected="rowSelected"
        @selection-changed="handleSelected"
      />
    </b-container>
    <b-container v-show="tab === 'viewer'" fluid>
      <basic-viewer v-if="pdfData" :pdf-data="pdfData" :width="width" :height="height" />
      <span v-else> No Data </span>
    </b-container>
    <el-dialog
      title="Report Options"
      size="tiny"
      :visible="visible"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <el-row type="flex" align="middle" element-loading-text='Saving...'>
        <el-col>
          <el-row type="flex" align="middle">
            <el-form :model="form" ref="authForm" v-if="selectedReportOptions">
              <el-form-item style="margin-bottom: 6px;">
                Do you wish to update the Report Options saved under {{ selectedReportOptions.report_name }}
              </el-form-item>
            </el-form>
            <el-form :model="form" ref="authForm" v-if="empty_error">
              <el-form-item style="margin-bottom: 6px;">
                Please select a set of Report Options to load.
              </el-form-item>
            </el-form>
            <el-form :model="form" ref="authForm" v-if="delete_error">
              <el-form-item style="margin-bottom: 6px;">
                Please select a set of Report Options to delete.
              </el-form-item>
            </el-form>
            <el-form :model="form" ref="authForm" v-if="!selectedReportOptions && !empty_error && !delete_error">
              <el-form-item style="margin-bottom: 6px;">
                <el-input placeholder="Name" v-model="savereportoptions" ref="nameField">
                </el-input>
              </el-form-item>
            </el-form>
          </el-row>
        </el-col>
      </el-row>
      <el-row v-if="error" type="flex" align="middle" justify="center" style="padding-top: 12px;">
        <el-tag type="danger">{{ error }}</el-tag>
      </el-row>
      <span slot="footer" class="dialog-footer">
        <el-button @click="visible = false" class="auth-btn" v-if="!empty_error && !delete_error">
          Cancel
        </el-button>
        <el-button @click="saveOptions" class="auth-btn" type="primary">
          OK
        </el-button>
      </span>
    </el-dialog>
    <el-dialog
      title="Delete Report Options"
      size="tiny"
      :visible="visible_delete"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false">
      <el-row type="flex" align="middle" element-loading-text='Saving...'>
        <el-col>
          <el-row type="flex" align="middle">
            <el-form :model="form" ref="authForm">
              <el-form-item style="margin-bottom: 6px;">
                Do you want to delete report options?
              </el-form-item>
            </el-form>
          </el-row>
        </el-col>
      </el-row>
      <el-row v-if="error" type="flex" align="middle" justify="center" style="padding-top: 12px;">
        <el-tag type="danger">{{ error }}</el-tag>
      </el-row>
      <span slot="footer" class="dialog-footer">
        <el-button @click="visible_delete = false" class="auth-btn">
          Cancel
        </el-button>
        <el-button @click="confirmDeleteOptions" class="auth-btn" type="primary">
          OK
        </el-button>
      </span>
    </el-dialog>
    <el-dialog
      title="Bulk Update"
      :visible.sync="bulkPopupModal"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <NewEntityForm
        :modalOpen="bulkPopupModal"
        :fields="bulkFields"
        :form.sync="bulkform"
        :idPrefix="'a-'"
        ref="bulkform"
      />
      <span slot="footer" class="dialog-footer">
        <el-row>
          <el-col :span="17" v-show="bulkSubmitting">
            <el-text type="warning">Please wait while changes are being applied. Do not refresh or navigate away.</el-text>
          </el-col>
          <el-col :span="bulkSubmitting ? 7 : 24">
            <el-button :disabled="bulkSubmitting" @click="closeBulkModal">Cancel</el-button>
            <el-button type="primary" :disabled="bulkSubmitting" @click="submitBulkForm">
              <b-spinner class="mr-2" small v-if="bulkSubmitting" />
              {{ bulkSubmitting ? 'Submitting' : 'Submit'}}
            </el-button>
          </el-col>
        </el-row>
      </span>
    </el-dialog>
    <el-dialog
      class="change-loss-dialog"
      size="tiny"
      title="Are you sure you want to leave?"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :visible="tabChangePopup">
      <el-row type="flex">
        <el-col>
          <el-row type="flex" align="middle">
            Changes you made will not be saved.
          </el-row>
        </el-col>
      </el-row>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeTabChangePopup">
          Cancel
        </el-button>
        <el-button type="primary" @click="confirmTabChange">
          Confirm
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
  /* eslint-disable no-unused-vars */
  import { sortObjectByProperty } from '@/utils/helpers/ObjectSorting'
  import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
  import { reportMixins } from '@/Mixins/Reports'
  import DateField from '@/components/InputComponents/DateField.vue'
  import { notify } from '@/utils/helpers/Notify'
  import NewEntityForm from '@/components/NewEntityForm.vue'
  import { AgGridVue } from 'ag-grid-vue'
  import 'ag-grid-enterprise'
  import AgGridColumns from '@/Mixins/SigmaClientNew/GeneralLedgerWorksheet/Columns'
  import AgGridMethodsMixins from '@/Mixins/SigmaClientNew/GeneralLedgerWorksheet/Methods'
  import BasicViewer from '@/components/Reports/BasicViewer.vue'
  import moment from 'moment'
  import { getHeaders } from '@/utils/api'
  import { BIcon, BIconPaperclip, BIconTable, BIconArrowsExpand, BIconArrowsCollapse, BIconArrowRepeat, BIconLayersFill, BIconBullseye, BIconCalendarFill, BIconPieChart, BIconPrinterFill, BIconCollection, BIconLock } from 'bootstrap-vue'
  import { buildLocalTree, createLocalTreeDatasource, getFilteredTreeTotal, getDisplayRowCountForNode, getNodeAtPath } from '@/Mixins/SigmaClientNew/GeneralLedgerWorksheet/Localtreedatasource.js'
  // A payment line's line_id can collide with a distribution line's, so every
  // lookup resolving a line_id back to a row must exclude payments — otherwise an
  // edit lands on the payment instead of the GL line the user changed.
  const PAYMENT_TRANS_TYPES = ['CK', 'MC', 'EFT', 'WIRE']
  const isNonPaymentLine = (line) => !!line && !PAYMENT_TRANS_TYPES.includes(line.trans_type)

// (adjust the path to wherever you put localTreeDatasource.js)
  // Reused across all per-account sorts. A single Intl.Collator is far faster
  // than String.localeCompare(x, 'en', { numeric: true }) called per comparison
  // (which re-parses the locale/options every time) and produces identical order.
  const TRANS_NUMBER_COLLATOR = new Intl.Collator('en', { numeric: true })

  export default {
    name: 'GlReport',
    components: {
      NewEntityForm,
      AgGridVue,
      DateField,
      notify,
      BasicViewer,
      BIcon,
      BIconPaperclip,
      BIconTable,
      BIconArrowRepeat,
      BIconLayersFill,
      BIconBullseye,
      BIconCalendarFill,
      BIconPrinterFill,
      BIconArrowsExpand,
      BIconPieChart,
      BIconArrowsCollapse,
      BIconCollection,
      BIconLock
    },
    mixins: [ AgGridColumns, AgGridMethodsMixins, reportMixins ],
    data () {
      const { getJasperHost } = this.$store.getters
      return {
        csvSubTotalByAccount: false,
        filteredAccounts: [],
        effectiveDateMandatory: false,
        // Grid related
        iteration: 0, // Key for Ag-Grid to refresh
        gridOptions: null,
        gridApi: null,
        columnApi: null,
        columnDefs: null,
        baseColumnDefs: [],
        pendingSortState: null,
        rowData: null,
        gridVisible: true,
        defaultColDef: null,
        autoGroupColumnDef: null,
        isLoading: true,
        isSaving: false,
        isPrinting: false,
        isWorksheet: false,
        isPrinted: false,
        tab: 'option',
        scope: this,
        width: '100%',
        pdfWidth: '100%',
        pdfHeight: '100%',
        workSheetData: [],
        jeLinesToBeUpdated: [],
        poLinesToBeUpdated: [],
        postedTransactionsToBeUpdated: [],
        unPostedTransactionsToBeUpdated: [],
        dcColumns: [],
        dcAllowedColumns: [],
        dcAllowedColumnMap: {
          account: 'Account',
          series: 'Series',
          location: 'Location',
          set: 'Set',
          description: 'Description',
          free_field_1: 'FF1',
          free_field_2: 'FF2',
          free_field_3: 'FF3',
          free_field_4: 'FF4',
          tax_code: 'Tax',
          asset: 'Asset'
        },
        pdfData: undefined,
        originalLines: '',
        displayOptions: {
          groupingLevel: 2,
          reportTemplateId: 1,
          hideDecimals: false,
          // PA-2251: off by default — the worksheet opens flat and sortable.
          // On restores the account tree, which is not sortable.
          groupByAccount: false,
          columns: [
            { name: 'line_desc', displayName: 'GL Description', display: true },
            { name: 'vendor_name', displayName: 'Vendor', display: true },
            { name: 'series', displayName: 'Ser', display: true },
            { name: 'location', displayName: 'Loc', display: true },
            { name: 'set', displayName: 'Set', display: true },
            { name: 'free_field_1', displayName: 'FF1', display: true },
            { name: 'free_field_2', displayName: 'FF2', display: true },
            { name: 'free_field_3', displayName: 'FF3', display: true },
            { name: 'free_field_4', displayName: 'FF4', display: true },
            { name: 'ins', displayName: 'Ins', display: true },
            { name: 'tax_code', displayName: '9', display: true },
            { name: 'asset', displayName: 'Asset', display: true },
            { name: 'doc_number', displayName: 'Doc #', display: true },
            { name: 'transaction_currency', displayName: 'Cur', display: true }
          ]
        },
        reportOptions: {
          transactionStatuses: [ 'posted', 'closed', 'reversed' ],
          accountQuickPick: 'all',
          vendorQuickPick: 'all',
          type: 'detail',
          dates: 'datesAll',
          sort: 'account',
          status: 'all',
          format: 'pdf',
          currencyFactor: undefined,
          report_currency_id: 1,
          includeunposted: false,
          summarizeLines: false,
          summaryType: 'one_line'
        },
        selectedCodes: undefined,
        allowEdit: false,
        report_group: 'General_Ledger',
        savereportoptions: '',
        isBusy: false,
        visible: false,
        empty_error: false,
        delete_error: false,
        selectedReportOptions: null,
        savedReportOptions: [],
        selectedOption: null,
        visible_delete: false,
        form: {save_options: { label: '', value: '' }},
        newAddedReport: undefined,
        reRender: 0,
        allCodes: {},    // Show codes, e.g. accounts, series, locations, sets, etc.
        jasperSettings: {
          reportServer: `${getJasperHost}rest_v2/reports/Revolution/Phoenix/`,
          reportName: 'general_ledger_detail'
        },
        vendors: undefined,
        transactionTypes: undefined,
        unserializedData: {      // Raw data from API
          aps: {
            headers: {},
            lines: {}
          },
          jes: {
            headers: {},
            lines: {}
          },
          pcs: {
            headers: {},
            lines: {}
          },
          prs: {
            headers: {},
            lines: {}
          },
          cks: {
            headers: {},
            lines: {}
          },
          pos: {
            headers: {},
            lines: {}
          },
          efts: {
            headers: {},
            lines: {}
          },
          wts: {
            headers: {},
            lines: {}
          },
          mcs: {
            headers: {},
            lines: {}
          }
        },
        reportOutputFormats: [
          { value: 'pdf', label: 'PDF' },
          { value: 'csv', label: 'CSV' }
        ],
        disableReportRun: false, // Disable run report button if false
        reportJson: undefined,
        prettyJson: undefined,
        showJson: false,
        reportNotif: undefined,
        collapseExpenses: false,
        selection: [],
        bulkPopupModal: false,
        bulkform: {
          account: '',
          line_desc: '',
          series: '',
          location: '',
          set: '',
          free_field_1: '',
          free_field_2: '',
          free_field_3: '',
          free_field_4: '',
          tax_code: '',
          asset: ''
        },
        bulkSubmitting: false,
        loading: false,
        userGridState: null,
        allUsersGridState: [],
        hasGridExists: false,
        filterState: null,
        tabChangePopup: false,
        tabToChange: null,
        instance: null,
        instanceHeaders: null,
        summarization: { value: false, type: 'one_line' },
        localTree: null,
        invalidCellKeys: [],
        collapsedGroupRoutes: [],
        isAutoExpanding: false,
        unpostedStatuses: ['OPEN']
      }
    },

    computed: {
      hasUpdateErrors () {
        return this.invalidCellKeys.length > 0
      },
      isGroupedByAccount () {
        return !!this.displayOptions.groupByAccount
      },
      // Non-empty while the Transaction Numbers box holds something we cannot parse.
      transactionNumbersError () {
        if (!this.selectedCodes) return ''
        return this.parseTransactionNumbers(this.selectedCodes.transaction_numbers).error
      },
      filteredOptions2 () {
        return this.allCodes.accounts.filter((option) => option.id !== this.selectedCodes.account_id)
      },
      username () { return this.$store.getters.getUser.username },
      includedTypes () {
        if (this.selectedCodes.source_code_id.length) {
          // if (this.selectedCodes.source_code_id.length == 1 && this.selectedCodes.source_code_id.includes(5))
          this.transactionTypes.jes.include = true
          // else this.transactionTypes.jes.include = true
          // if (this.selectedCodes.source_code_id.includes(5)) {
          //   this.transactionTypes.prs.include = true
          // } else this.transactionTypes.prs.include = false
        }
        return Object.values(this.transactionTypes).filter(types => types.include === true).map(type => type.name)
      },
      accounts () {
        const rawAccounts = Array.isArray(this.allCodes.accounts) ? this.allCodes.accounts : []
        return rawAccounts
          .filter((account) => account && account.type && account.type.toLowerCase() !== 'header')
          .map((account) => ({
            ...account,
            display_name: `${account.code} - ${account.name}`
          }))
      },
      currencies () {
        if (typeof this.allCodes.currencies !== 'undefined' && this.allCodes.currencies.length > 0) {
          return sortObjectByProperty(this.allCodes.currencies, 'id')
        } else {
          return []
        }
      },
      seriesCodes () {
        const selected = (this.allCodes.series || []).filter((ser) => this.selectedCodes.series_id.includes(ser.id))
        if (selected.length === 0) return 'All'
        else return selected.map((ser) => ser.code).join(', ')
      },
      locationCodes () {
        const selected = (this.allCodes.locations || []).filter((loc) => this.selectedCodes.location_id.includes(loc.id))
        if (selected.length === 0) return 'All'
        else return selected.map((loc) => loc.code).join(', ')
      },
      setCodes () {
        const selected = (this.allCodes.sets || []).filter((set) => this.selectedCodes.set_id.includes(set.id))
        if (selected.length === 0) return 'All'
        else return selected.map((set) => set.code).join(', ')
      },
      freeFieldCodes () {
        const selected = (this.allCodes.freefield1 || []).filter((ff) => this.selectedCodes.free_field_1_id.includes(ff.id) || this.selectedCodes.free_field_2_id.includes(ff.id) || this.selectedCodes.free_field_3_id.includes(ff.id) || this.selectedCodes.free_field_4_id.includes(ff.id))
        if (selected.length === 0) return 'All'
        else return selected.map((ff) => ff.code).join(', ')
      },
      sourceCodes () {
        const selected = (this.allCodes.sourcecodes || []).filter((sc) => this.selectedCodes.source_code_id.includes(sc.id))
        if (selected.length === 0) return 'All'
        else return selected.map((sc) => sc.code).join(', ')
      },
      seriesNames () {
        const selected = (this.allCodes.series || []).filter((ser) => this.selectedCodes.series_id.includes(ser.id))
        if (selected.length === 0) return ''
        else return selected.map((ser) => ser.name).join(', ')
      },
      locationNames () {
        const selected = (this.allCodes.locations || []).filter((loc) => this.selectedCodes.location_id.includes(loc.id))
        if (selected.length === 0) return ''
        else return selected.map((loc) => loc.name).join(', ')
      },
      setNames () {
        const selected = (this.allCodes.sets || []).filter((set) => this.selectedCodes.set_id.includes(set.id))
        if (selected.length === 0) return ''
        else return selected.map((set) => set.name).join(', ')
      },
      dateStart () {
        switch (this.reportOptions.dates) {
          case 'datesPeriod':
            return (this.allCodes.periods.find(({id}) => id === this.selectedCodes.period_start) || {}).start || '0000/00/00'

          case 'datesEffective':
            return this.selectedCodes.effective_date_start || '0000/00/00'

          case 'datesPosted':
            return this.selectedCodes.time_posted_start || '0000/00/00'

          default:
            return 'ALL'
        }
      },
      dateEnd () {
        switch (this.reportOptions.dates) {
          case 'datesPeriod':
            return (this.allCodes.periods.find(({id}) => id === this.selectedCodes.period_end) || {}).end || '9999/99/99'

          case 'datesEffective':
            return this.selectedCodes.effective_date_end || '9999/99/99'

          case 'datesPosted':
            return this.selectedCodes.time_posted_end || '9999/99/99'

          default:
            return 'ALL'
        }
      },
      showUnposted () {
        return this.$store.getters.getUiSettings && this.$store.getters.getUiSettings['GL_REPORT_DEFAULT_SETTINGS'] && JSON.parse(this.$store.getters.getUiSettings['GL_REPORT_DEFAULT_SETTINGS']).showUnposted ? JSON.parse(this.$store.getters.getUiSettings['GL_REPORT_DEFAULT_SETTINGS']).showUnposted : false
      },
      hideDecimals () {
        return this.$data.displayOptions.hideDecimals
      },
      height () {
        // Reducing by 3 % to handle sigma-frontend navbar
        return window.innerHeight * 0.88 + 'px'
      },
      bulkFields () {
        return [
          {
            label: 'Account',
            name: 'account',
            fieldType: 'select',
            filterable: true,
            disabled: this.bulkSubmitting,
            choices: this.allCodes.accounts ? [
              ...this.allCodes.accounts.filter(account => account.postable).map(account => {
                return { display_name: account.code + ' - ' + account.name, value: account.code }
              })
            ] : []
          },
          {
            label: 'Description',
            name: 'line_desc',
            type: 'string',
            fieldType: 'input',
            disabled: this.bulkSubmitting
          },
          {
            label: 'Series',
            name: 'series',
            fieldType: 'select',
            filterable: true,
            disabled: this.bulkSubmitting,
            choices: this.allCodes.series ? [
              ...this.allCodes.series.filter(ser => ser.active).map(ser => {
                return { display_name: ser.code + ' - ' + ser.name, value: ser.code }
              })
            ] : []
          },
          {
            label: 'Location',
            name: 'location',
            fieldType: 'select',
            filterable: true,
            disabled: this.bulkSubmitting,
            choices: this.allCodes.locations ? [
              ...this.allCodes.locations.filter(loc => loc.active).map(loc => {
                return { display_name: loc.code + ' - ' + loc.name, value: loc.code }
              })
            ] : []
          },
          {
            label: 'Set',
            name: 'set',
            fieldType: 'select',
            filterable: true,
            disabled: this.bulkSubmitting,
            choices: this.allCodes.sets ? [
              ...this.allCodes.sets.filter(set => set.active).map(set => {
                return { display_name: set.code + ' - ' + set.name, value: set.code }
              })
            ] : []
          },
          {
            label: 'FF1',
            name: 'free_field_1',
            fieldType: 'select',
            filterable: true,
            disabled: this.bulkSubmitting,
            choices: this.allCodes.freefield1 ? [
              { display_name: 'Clear Value - No Value', value: 'N/A' }, ...this.allCodes.freefield1.filter(ff => ff.active).map(ff => {
                return { display_name: ff.code + ' - ' + ff.name, value: ff.code }
              })
            ] : []
          },
          {
            label: 'FF2',
            name: 'free_field_2',
            fieldType: 'select',
            filterable: true,
            disabled: this.bulkSubmitting,
            choices: this.allCodes.freefield1 ? [
              { display_name: 'Clear Value - No Value', value: 'N/A' }, ...this.allCodes.freefield1.filter(ff => ff.active).map(ff => {
                return { display_name: ff.code + ' - ' + ff.name, value: ff.code }
              })
            ] : []
          },
          {
            label: 'FF3',
            name: 'free_field_3',
            fieldType: 'select',
            filterable: true,
            disabled: this.bulkSubmitting,
            choices: this.allCodes.freefield1 ? [
              { display_name: 'Clear Value - No Value', value: 'N/A' }, ...this.allCodes.freefield1.filter(ff => ff.active).map(ff => {
                return { display_name: ff.code + ' - ' + ff.name, value: ff.code }
              })
            ] : []
          },
          {
            label: 'FF4',
            name: 'free_field_4',
            fieldType: 'select',
            filterable: true,
            disabled: this.bulkSubmitting,
            choices: this.allCodes.freefield1 ? [
              { display_name: 'Clear Value - No Value', value: 'N/A' }, ...this.allCodes.freefield1.filter(ff => ff.active).map(ff => {
                return { display_name: ff.code + ' - ' + ff.name, value: ff.code }
              })
            ] : []
          },
          {
            label: 'Tax Code (9)',
            name: 'tax_code',
            fieldType: 'select',
            filterable: true,
            disabled: this.bulkSubmitting,
            idNum: 9,
            choices: this.allCodes.taxcodes ? [
              { display_name: 'Clear Value - No Value', value: 'N/A' }, ...this.allCodes.taxcodes.filter(taxcode => taxcode.active).map(taxcode => {
                return { display_name: taxcode.code + ' - ' + taxcode.name, value: taxcode.code }
              })
            ] : []
          },
          {
            label: 'Asset',
            name: 'asset',
            dataType: 'boolean',
            fieldType: 'checkbox',
            disabled: this.bulkSubmitting
          }
        ]
      }
    },
    methods: {
      // Parse the "Transaction Numbers" box into the two shapes the BE filters on.
      parseTransactionNumbers (raw) {
        const result = { ids: [], ranges: [], error: '' }
        if (!raw || !String(raw).trim()) return result
        for (const token of String(raw).split(',')) {
          const part = token.trim()
          if (!part) continue
          const range = part.match(/^(\d+)\s*-\s*(\d+)$/)
          if (range) {
            const from = Number(range[1])
            const to = Number(range[2])
            result.ranges.push(from <= to ? [from, to] : [to, from])
          } else if (/^\d+$/.test(part)) {
            result.ids.push(Number(part))
          } else {
            result.error = `"${part}" is not a valid transaction number or range. Use e.g. 1, 2, 5-10, 12.`
            return { ids: [], ranges: [], error: result.error }
          }
        }
        return result
      },
      changeTab (tab) {
        if ((this.jeLinesToBeUpdated.length || this.poLinesToBeUpdated.length || this.unPostedTransactionsToBeUpdated.length) && tab !== this.tab) {
          this.tabToChange = tab
          this.tabChangePopup = true
        } else {
          this.tab = tab
        }
      },
      closeTabChangePopup () {
        this.tabChangePopup = false
      },
      confirmTabChange () {
        this.tabChangePopup = false
        this.tab = this.tabToChange
      },
      checkCodeValidity () {
        const fromValue = this.selectedCodes.account_id2 !== '' ? this.allCodes.accounts.filter((account) => account.id === this.selectedCodes.account_id2) : this.allCodes.accounts.filter((element, index) => index === 0)
        const toValue = this.selectedCodes.account_id1 !== '' ? this.allCodes.accounts.filter((account) => account.id === this.selectedCodes.account_id1) : this.allCodes.accounts.filter((element, index) => index === this.allCodes.accounts.length - 1)
        if (fromValue && toValue) {
          if (fromValue[0].code > toValue[0].code) {
            this.disableReportRun = true
            notify(this, 'error', 'The To Account cannot be before the From Account. Please update your selection.')
          } else {
            this.selectedCodes.account_id = []
            if (this.selectedCodes.account_id1 === '' || this.selectedCodes.account_id2 === '') {
              this.selectedCodes.account_id = this.allCodes.accounts.map((account) => account.id)
            } else {
              this.disableReportRun = false
              this.filteredAccounts = this.allCodes.accounts.filter((account) => account.code >= fromValue[0].code && account.code <= toValue[0].code)
              this.selectedCodes.account_id = this.filteredAccounts.map((account) => account.id)
            }
          }
        }
      },
      resetComponent () {
        this.clearStore()
        this.resetFilters()
      },
      clearStore () {
        if (this.gridApi) {
          try {
            this.gridApi.destroy()
          } catch (e) {
            // grid may already be tearing down — safe to ignore
          }
        }
        this.selection = []
        this.gridApi = null
        this.columnApi = null
        this.localTree = null
        if (this.gridOptions) {
          this.gridOptions.api = null
          this.gridOptions.columnApi = null
          this.gridOptions.serverSideDatasource = null   // section 18a — the actual root cause fix
        }
        this.rowData = null
        this.workSheetData = []
        this.originalLines = ''
        this.pdfData = undefined
        this.prettyJson = undefined
        // Collapsed groups belong to the grid instance just destroyed.
        this.collapsedGroupRoutes = []
        this.isAutoExpanding = false
      },
      updateReportType (type) {
        this.jasperSettings.reportName = 'general_ledger_' + type
      },
      updateReportDates (type) {
        this.selectedCodes.period_start = this.selectedCodes.period_end = this.selectedCodes.time_posted_start = this.selectedCodes.time_posted_end = this.selectedCodes.effective_date_start = this.selectedCodes.effective_date_end = undefined
      },
      updateAccounts (type) {
        if (!type) return false
        this.selectedCodes.account_id = []
        switch (type) {
          case 'none':
          case 'all':
          case 'custom':
          case 'range':
            this.disableReportRun = false
            break
          case 'expenses':
            this.disableReportRun = false
            this.selectedCodes.account_id = this.allCodes.accounts.filter(({type}) => type.toLowerCase().match('expense')).map(acct => acct.id)
            break
          case 'balance':
            this.disableReportRun = false
            this.selectedCodes.account_id = this.allCodes.accounts.filter(({type}) => {
              if (type.toLowerCase().match('asset') || type.toLowerCase().match('liability') || type.toLowerCase().match('income')) return true
            }).map(acct => acct.id)
            break
          default:
            return undefined
        }
        // The transfer table is one tick behind. Force update now
        this.$forceUpdate()
        return true
      },
      updateVendors (type) {
        if (!type) return false
        switch (type) {
          case 'none':
            this.disableReportRun = true
            this.selectedCodes.vendor_id = []
            break
          case 'all':
            this.disableReportRun = false
            this.selectedCodes.vendor_id = []
            break
          case 'custom':
            this.disableReportRun = false
            break
          default:
            return undefined
        }
        // The transfer table is one tick behind. Force update now
        this.$forceUpdate()
        return true
      },
      addExpenseAccounts () {
        this.selectedCodes.account_id = this.allCodes.accounts.filter(({type}) => type.match('EXPENSE')).map(acct => acct.id)
      },
      includeAps ({target}) {
        this.transactionTypes.aps.include = target.checked
      },
      includeJes ({target}) {
        this.transactionTypes.jes.include = target.checked
      },
      includePcs ({target}) {
        this.transactionTypes.pcs.include = target.checked
      },
      includePrs ({target}) {
        this.transactionTypes.prs.include = target.checked
      },
      includeCks ({target}) {
        this.transactionTypes.cks.include = target.checked
      },
      includePos ({target}) {
        this.transactionTypes.pos.include = target.checked
      },
      includeEfts ({target}) {
        this.transactionTypes.efts.include = target.checked
      },
      includeWts ({target}) {
        this.transactionTypes.wts.include = target.checked
      },
      includeMcs ({target}) {
        this.transactionTypes.mcs.include = target.checked
      },
      includeAchs ({target}) {
        this.transactionTypes.achs.include = target.checked
      },
      includeDds ({target}) {
        this.transactionTypes.dds.include = target.checked
      },
      includeNas ({target}) {
        this.transactionTypes.nas.include = target.checked
      },
      selectReportFormat ({target}) {
        if (target.checked) {
          this.reportOptions.format = 'csv'
        } else {
          this.reportOptions.format = 'pdf'
        }
      },
      buildCsvData (sortedAccounts) {
        if (!sortedAccounts || !Array.isArray(sortedAccounts)) return false

        let detailedColumns = { 'Company': 'company_name', 'Production': 'production_name', 'Transaction Type': 'source_code', 'Transaction ID': 'trans_number', 'Transaction Description': 'transaction_description', 'Transaction Line Description': 'line_desc', 'Account': 'account', 'Account Description': 'account_name', 'Vendor Code': 'transaction_vendor', 'Vendor Name': 'vendor_name', 'Series': 'series', 'Location': 'location', 'Set': 'set', 'FF1': 'free_field_1', 'FF2': 'free_field_2', 'FF3': 'free_field_3', 'FF4': 'free_field_4', 'Tax Code': 'tax_code', 'Asset': 'asset', 'Document Number': 'doc_number', 'Document Date': 'doc_date', 'Posting Date': 'transaction_time_posted', 'Effective Date': 'transaction_effective_date', 'Period': 'transaction_period_id', 'PO Number': 'po_number', 'Check Number': 'payment_number', 'Check Amount': 'transaction_payment_amount', 'Bank Recon': 'recon', 'Cur': 'transaction_currency', 'Amount': 'amount' }
        let summaryColumns = { 'Account': 'account_number', 'Account Description': 'account_desc', 'Amount': 'account_total' }

        // TODO: Sipra Output
        let columnMap = (this.reportOptions.type === 'summary') ? summaryColumns : detailedColumns

        return new Promise((resolve, reject) => {
          let ledgerData = []

          sortedAccounts.forEach(accountCode => {
            if (this.reportOptions.type === 'summary') {
              accountCode.transaction = undefined
              ledgerData.push(accountCode)
            } else {
              accountCode.transaction.forEach(row => ledgerData.push(row))
            }
          })

          let finalCsv
          this.disableReportRun = false
          ledgerData.forEach(data => {
            data.amount = data.amount ? parseFloat(data.amount).toFixed(2) : data.amount
            data.transaction_payment_amount = data.transaction_payment_amount ? parseFloat(data.transaction_payment_amount).toFixed(2) : data.transaction_payment_amount
          })
          if (this.csvSubTotalByAccount) { finalCsv = this.createGroupByAccountCsv(ledgerData, columnMap) } else { finalCsv = this.createCsv(ledgerData, columnMap) }

          if (finalCsv) resolve(finalCsv)
          else reject('Could not build CSV.')
        })
      },

      buildGlReport (sortedAccounts) {
        return new Promise((resolve, reject) => {
          let json = {
            report: {
              report_title: `General Ledger ${capitalizeFirstLetter(this.reportOptions.type)}`,
              report_subtitle: '',
              report_name: 'general_ledger_detail',
              printed_by: this.username,
              date_run: this.today(),
              company: (this.selectedCodes.company_id) ? (this.allCodes.companies.find(({id}) => id === this.selectedCodes.company_id).name) : this.allCodes.companies[0].name,
              project:
                this.selectedCodes.production_id && this.selectedCodes.production_id.length
                ? this.allCodes.productions.filter((prod) => this.selectedCodes.production_id.includes(prod.id)).map((prod) => prod.name).join(', ')
                : this.allCodes.productions[0].name,
              currency: (this.reportOptions.report_currency_id) ? (this.allCodes.currencies.find(({id}) => id === this.reportOptions.report_currency_id).code) : this.currencies[0].code,
              period: this.dateStart + ' - ' + this.dateEnd,
              date_start: this.dateStart,
              date_end: this.dateEnd,
              grand_total: '',
              transactions: 'All',
              // Episodes is legacy
              episodes: (this.selectedCodes.series_id && this.selectedCodes.series_id.length > 0) ? (this.allCodes.series.find(({id}) => this.selectedCodes.series_id.includes(id)).code) : 'All',
              series: this.seriesCodes,
              seriesNames: this.seriesNames,
              locations: this.locationCodes,
              locationNames: this.locationNames,
              sets: this.setCodes,
              setNames: this.setNames,
              free_fields: this.freeFieldCodes,
              sourcecodes: this.sourceCodes,
              accounts: capitalizeFirstLetter(this.reportOptions.accountQuickPick),
              data: []
            }
          }
          // Put finished data here
          let ledgerData = []
          // TODO Use selected accounts instead of all accounts
          this.allCodes.accounts.map((data) => {
            // Place account code into report
            let account = {}
            account.account_desc = data.name
            account.account_number = data.code
            account.account_id = data.id
            account.account_parent = data.parent
            // Now look through actual data and put into the proper group
            if (sortedAccounts.current[data.code]) {
              let transactions = sortedAccounts.current[data.code]
              account.account_total = this.sumRecursive(transactions, 'amount', null, null, null, false)
              transactions.forEach((trn) => {
                trn.trans_type = trn.source_code
                trn.amount = parseFloat(trn.amount).toFixed(2)
              })
              account.transaction = transactions.sort((a, b) => TRANS_NUMBER_COLLATOR.compare(a.trans_number, b.trans_number))
              ledgerData.push(account)
            }
          })
          json.report.grand_total = this.sumRecursive(ledgerData, 'account_total', null, null, null, false)
          json.report.data = ledgerData
          resolve(json)
        })
      },
      // Build a slim payload for the Jasper `addJson` call. The glbible rows
      // carry ~50 fields each, but the general_ledger_detail/summary template
      // only renders the ones below. Projecting to just these before the POST
      // (which JSON.stringifies the whole thing) is what keeps the serialization
      // from spiking ~1GB.
      //
      // This is DESTRUCTIVE on purpose: it frees each full transaction as soon
      // as its slim copy is made (and drops the source arrays), so the full and
      // slim datasets never fully coexist. That keeps the peak ~1x instead of
      // ~1.4x, which is what was crashing the tab before the request even fired.
      // Safe because `json` (and the resSorted it shares rows with) is discarded
      // right after this in the PDF path — nothing reads it again.
      buildJasperPayload (json) {
        const TXN_FIELDS = [
          'trans_number', 'trans_type', 'line_desc', 'vendor_name',
          'series', 'location', 'set',
          'free_field_1', 'free_field_2', 'free_field_3', 'free_field_4',
          'ins', 'tax_code', 'asset',
          'doc_number', 'transaction_effective_date', 'transaction_period_id',
          'po_number', 'payment_number', 'amount', 'transaction_currency'
        ]
        const src = json.report.data || []
        const data = []
        for (const account of src) {
          const txns = account.transaction || []
          const slimTxns = new Array(txns.length)
          for (let i = 0; i < txns.length; i++) {
            const trn = txns[i]
            const slimTrn = {}
            for (const field of TXN_FIELDS) slimTrn[field] = trn[field]
            slimTxns[i] = slimTrn
            txns[i] = null // release the full row now that it is copied
          }
          account.transaction = null // release the full row array
          data.push({
            account_number: account.account_number,
            account_desc: account.account_desc,
            account_total: account.account_total,
            transaction: slimTxns
          })
        }
        return { report: { ...json.report, data } }
      },
      async generateReport () {
        this.loading = true
        this.disableReportRun = true
        const project =
            this.selectedCodes.production_id && this.selectedCodes.production_id.length
            ? this.allCodes.productions.filter((prod) => this.selectedCodes.production_id.includes(prod.id)).map((prod) => prod.name).join(', ')
            : this.allCodes.productions[0].name
        const fileName = `GL-Bible-${capitalizeFirstLetter(this.reportOptions.type)}_${project}_${moment.utc(new Date()).local().format('MM-DD-YYYY-HHmmss')}`
        // Free the worksheet grid + its dataset BEFORE fetching/processing the
        // report. generateReport re-fetches glbible and builds a fresh report
        // structure, so it does not need the live worksheet — without this the
        // two large datasets coexist in memory and crash the tab.
        this.clearStore()
        this.gridVisible = false
        await this.$nextTick()
        // The unmount above only *frees* the worksheet grid's memory; the browser
        // reclaims it on a later GC pass. Yield briefly so that collection can run
        // BEFORE we allocate the report dataset — otherwise the freed-but-not-yet-
        // collected worksheet memory overlaps the new report data and pushes the
        // peak over the tab limit (the "works 50-50" symptom). ~400ms is well
        // within a normal report's runtime and is invisible to the user.
        await new Promise(resolve => setTimeout(resolve, 400))
        this.summarization.value = this.reportOptions.summarizeLines
        if (this.reportOptions.format === 'csv') {
          this.getGlReportData()
            .then((resSorted) => {
              this.loading = false
              return this.buildGlReport(resSorted)
            })
            .then((resReport) => {
              return this.buildCsvData(resReport.report.data)
            })
            .then((resCsv) => {
              this.prettyJson = resCsv
              this.createNotification('Success', 'CSV is Ready. ', 'success', false, true, fileName)
              return true
            })
            .catch(error => {
              this.loading = false
              console.log(error)
            })
            .finally(() => {
              this.disableReportRun = false
              this.loading = false
            })
        } else {
          this.getGlReportData()
            .then((resSorted) => {
              return this.buildGlReport(resSorted)
            })
            .then(json => {
              if (this.showJson) this.prettyJson = JSON.stringify(json, null, 2)
              return this.getJsonUrl(this.buildJasperPayload(json))
            })
            .then(resJsonUrl => {
              // PDF version of the report
              let report = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.pdf?net.sf.jasperreports.json.source=' + resJsonUrl
              // HTML version of the report
              let preview = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.html?net.sf.jasperreports.json.source=' + resJsonUrl
              return this.getReportUrl(report)
            })
            .then(reportUrl => {
              this.loading = false
              this.createNotification('Success', 'Report is Ready. ', 'success', reportUrl, false, fileName)
            })
            .catch(error => {
              this.loading = false
              console.log(error)
            })
            .finally(() => {
              this.disableReportRun = false
              this.loading = false
            })
        }
      },

      refreshWorksheet () {
        this.buildWorkSheet(true)
      },

      async buildWorkSheet (keepFilter = true) {
        if (!keepFilter) {
          this.filterState = null
        }
        this.summarization.value = false
        this.isSaving = false
        this.isWorksheet = true
        this.isLoading = true
        this.tab = 'report'
        // Re-arm both progress cues per build — neither was, so only the very
        // first build showed anything. Cleared in onFirstDataRendered.
        this.isLoading = true
        this.loading = true
        // Fully tear down the previous grid BEFORE fetching new data. Returning
        // to the options tab does not destroy the component, so beforeDestroy's
        // clearStore never runs between builds. clearStore() purges the row
        // nodes, and toggling gridVisible off + awaiting nextTick unmounts the
        // ag-grid instance entirely — so the old dataset is released and its
        // grid destroyed before we allocate/fetch the next one, instead of the
        // new data landing on top of a still-live grid.
        this.clearStore()
        // localTree is gone, so re-mount with sorting off until the datasource arrives.
        this.columnDefs = this.buildWorksheetColumnDefs()
        this.gridVisible = false
        await this.$nextTick()
        this.iteration++
        // Re-mount a fresh, empty grid instance now; the fetched rows populate
        // it once they arrive.
        this.gridVisible = true
        const self = this
        // Tag this build. If the user switches back to the options tab and
        // triggers another build before this async chain finishes, the newer
        // build bumps `iteration` — making every stage below a no-op for this
        // (now stale) run, so its results can never be dumped onto the current
        // grid "on top" of the newer data.
        const buildToken = this.iteration
        this.jeLinesToBeUpdated = []
        this.poLinesToBeUpdated = []
        this.postedTransactionsToBeUpdated = []
        this.unPostedTransactionsToBeUpdated = []
        this.displayOptions.groupingLevel = this.isGroupedByAccount ? 2 : 1
        this.getGlReportData()
          .then((resSorted) => {
            // Stale build superseded by a newer one — drop it before it can
            // write workSheetData / originalLines in buildWorkSheetReport.
            if (this.iteration !== buildToken) return null
            return this.buildWorkSheetReport(resSorted)
          })
          .then(json => {
            if (self.iteration !== buildToken || !json) return null
            let glData = {
              rows: json,
              displayOptions: this.displayOptions,
              reportOptions: this.reportOptions
            }
            // this.rowData = glData.rows
            // localTree now exists, so flat mode's columns become sortable.
            this.columnDefs = this.buildWorksheetColumnDefs()
            this.applyWorksheetDatasource()
            this.updateGrandTotalRow()
            return glData.rows
          })
          .catch(error => {
            if (self.iteration !== buildToken) return
            self.isWorksheet = false
            self.tab = 'option'
            self.rowData = null
            console.log(error)
          })
          .finally(() => {
            if (self.iteration !== buildToken) return
            self.isLoading = false
            if (self.reportOptions.type === 'summary' && self.isGroupedByAccount) {
              self.toggleCollapse()
            } else if (self.gridApi) {
              self.forceFullExpand(self.gridApi)
            }
            // Backstop: onFirstDataRendered never fires on an empty or failed
            // build, which would leave the overlay up and the tabs disabled.
            self.loading = false
            self.isLoading = false
          })
      },

      buildWorkSheetReport (sortedAccounts) {
        return new Promise((resolve, reject) => {
          let glData = []
          this.allCodes.accounts.map((data) => {
            let transactions = []
            if (sortedAccounts.current[data.code]) {
              transactions = sortedAccounts.current[data.code].sort((a, b) => TRANS_NUMBER_COLLATOR.compare(a.trans_number, b.trans_number))
              transactions.forEach((trn, n) => {
                trn.type = 'grand_total'
                trn.grandparent = data.code
                trn.grandparentDesc = ''
                trn.code = n + '__' + trn.trans_number
                trn.line_desc = trn.line_desc
                trn.reportGroup = 'GRAND TOTAL'
                trn.groupAccount = data.code
                glData.push(trn)
              })
            }
          })
          // Snapshot ONLY the fields the save/offset logic reads, instead of
          // deep-copying every field of every row. Captured here at build time
          // so the values stay pristine (unaffected by later in-grid edits) —
          // save behaviour is unchanged, but the retained copy is far smaller.
          // Skip it entirely for read-only users: originalLines is consumed only
          // by the save flow, and Save is hidden unless allowEdit is true, so
          // there is no reason to build/retain this snapshot for viewers.
          if (this.allowEdit) {
            this.originalLines = JSON.stringify(glData.map((line) => ({
              account_id: line.account_id,
              amount_native: line.amount_native,
              asset: line.asset,
              dc_line_id: line.dc_line_id,
              doc_number: line.doc_number,
              free_field_1_id: line.free_field_1_id,
              free_field_2_id: line.free_field_2_id,
              free_field_3_id: line.free_field_3_id,
              free_field_4_id: line.free_field_4_id,
              line_desc: line.line_desc,
              line_id: line.line_id,
              location_id: line.location_id,
              other_name: line.other_name,
              series_id: line.series_id,
              set_id: line.set_id,
              tax_code_id: line.tax_code_id,
              third_party_vendor_id: line.third_party_vendor_id,
              transaction_batch_id: line.transaction_batch_id,
              transaction_ledger_id: line.transaction_ledger_id,
              transaction_period_id: line.transaction_period_id,
              trans_type: line.trans_type
            })))
          }
          // Freeze the rows array so Vue does NOT make the (potentially huge)
          // dataset deeply reactive — that reactivity is what inflates a few MB
          // of data into multiple GB. ag-grid manages rows internally and needs
          // no reactivity; individual row objects stay mutable so editing works.
          this.workSheetData = Object.freeze(glData)
          this.getDcSettings()
          this.localTree = buildLocalTree(glData)
          resolve(glData)
        })
      },

      toggleExpand () {
        if (!this.gridApi) return
        this.forceFullExpand(this.gridApi)
        this.displayOptions.groupingLevel = 2
      },

      toggleCollapse () {
        if (!this.gridApi) return
        this.gridApi.forEachNode((node) => {
          if (node.level === 1) node.setExpanded(false)
        })
        // this.gridOptions.api.onGroupExpandedOrCollapsed()
        this.displayOptions.groupingLevel = 1
      },

      // Opens the source transaction in a new tab. Shared by the auto group column
      // (grouped) and the Trans # column (flat) so drill-down behaves the same.
      openTransaction (params) {
        if (!params || !params.data) return
        if (!['PO', 'AP', 'PC', 'JE', 'PR', 'DC'].includes(params.data.trans_type)) return
        let transactionType = 'aps'
        const transactionId = params.data.subledger_id ? params.data.subledger_id : params.data.trans_number
        switch (params.data.trans_type.toLowerCase()) {
          case 'po':
            transactionType = 'pos'
            break
          case 'ap':
            transactionType = 'aps'
            break
          case 'pc':
            transactionType = 'pettycash'
            break
          case 'je':
          case 'dc':
            transactionType = 'jes'
            break
          case 'pr':
            transactionType = 'prs'
            break
        }
        const link = document.createElement('a')
        link.setAttribute('href', `/${transactionType}/${transactionId}`)
        link.setAttribute('target', '_blank')
        document.body.appendChild(link)
        link.click()
        link.remove()
      },
      buildWorksheetColumnDefs () {
        const grouped = this.isGroupedByAccount
        const sortable = !grouped && !!this.localTree
        return this.baseColumnDefs.map((column) => {
          const columnDef = { ...column, sortable }
          if (column.colId === 'reportGroup' || column.colId === 'groupAccount') {
            columnDef.hide = true
            columnDef.rowGroup = grouped
            if (!grouped) delete columnDef.rowGroupIndex
          }
          if (column.colId === 'trans_number') columnDef.hide = grouped
          return columnDef
        })
      },

      // Single place that hands the grid its rows. Setting a datasource discards
      // the store, so callers must restore expansion/filter afterwards.
      applyWorksheetDatasource () {
        if (!this.localTree) return
        this.gridOptions.serverSideDatasource = createLocalTreeDatasource(this.localTree, { flat: !this.isGroupedByAccount })
        if (this.gridApi) {
          this.gridApi.setServerSideDatasource(this.gridOptions.serverSideDatasource)
          this.applyPendingSortState()
        }
      },

      // Replays the sort held back by restoreGridState. Only safe once a datasource
      // exists: ag-grid's SSRM sort listener reads an undefined getParams() before.
      applyPendingSortState () {
        const pending = this.pendingSortState
        this.pendingSortState = null
        if (!pending || this.isGroupedByAccount) return
        if (!this.columnApi || typeof this.columnApi.applyColumnState !== 'function') return
        this.columnApi.applyColumnState({ state: pending })
      },

      // Flat mode has no GRAND TOTAL band, so the total is pinned to the bottom
      // instead — following the active filter, like the group bands do.
      updateGrandTotalRow () {
        if (!this.gridApi) return
        if (this.isGroupedByAccount) {
          this.gridApi.setPinnedBottomRowData([])
          return
        }
        const filterModel = this.gridApi.getFilterModel()
        this.gridApi.setPinnedBottomRowData([{
          __isGrandTotal: true,
          trans_number: 'GRAND TOTAL',
          amount: getFilteredTreeTotal(this.localTree, filterModel)
        }])
      },

      async onToggleGroupByAccount (value) {
        // b-form-checkbox's change fires before v-model lands, so drive off the
        // event value and keep displayOptions in step for the computed getter.
        this.displayOptions.groupByAccount = typeof value === 'boolean' ? value : !!this.displayOptions.groupByAccount
        this.displayOptions.groupingLevel = this.isGroupedByAccount ? 2 : 1
        // Nothing built yet — the next build picks the mode up on its own.
        if (!this.gridApi || !this.localTree) return
        this.saveFilterState()
        // Grouped mode is not sortable, so an inherited sort has to go with it.
        if (this.isGroupedByAccount && typeof this.columnApi.applyColumnState === 'function') {
          this.columnApi.applyColumnState({ defaultState: { sort: null } })
        }
        this.columnDefs = this.buildWorksheetColumnDefs()
        // Let setColumnDefs land first: the datasource shape must match row-group columns the grid will ask for.
        await this.$nextTick()
        this.applyWorksheetDatasource()
        if (this.isGroupedByAccount) this.forceFullExpand(this.gridApi)
        if (this.filterState && Object.keys(this.filterState).length) {
          this.gridApi.setFilterModel(this.filterState)
        }
        this.updateGrandTotalRow()
        this.saveGridState()
      },

      async saveOptions () {
        if (this.isBusy) return
        if (this.empty_error || this.delete_error) {
          this.visible = false
          this.empty_error = false
          this.delete_error = false
          return
        }
        this.isBusy = true
        if (this.selectedReportOptions) {
          await this.updateReportOptions({
            data: {
              report_group: this.report_group,
              report_name: this.selectedReportOptions.report_name,
              filter: { reportOptions: this.reportOptions, selectedCodes: this.selectedCodes, transactionTypes: this.transactionTypes }
            },
            id: this.selectedReportOptions.id })
        } else {
          if (!this.savereportoptions) {
            this.$notify({ title: 'Error', message: 'Report Name is required', type: 'error' })
            return
          }
          this.newAddedReport = await this.saveReportOptions({
            report_group: this.report_group,
            report_name: this.savereportoptions,
            filter: { reportOptions: this.reportOptions, selectedCodes: this.selectedCodes, transactionTypes: this.transactionTypes }
          })
          this.selectedOption = this.newAddedReport.id
        }
        this.visible = false
        this.$notify({ title: 'Success', message: 'Report Options Saved', type: 'success' })
        await this.getOptions()
        this.empty_error = false
        this.delete_error = false
        this.isBusy = false
        this.savereportoptions = ''
      },
      openSaveOption () {
        this.visible = true
      },
      async getOptions () {
        try {
          const savedReportOptions = await this.getReportOptions()
          this.savedReportOptions = savedReportOptions.results
        } catch (e) {
          console.log(e)
        }
      },
      async loadOptions () {
        if (this.selectedReportOptions && this.selectedReportOptions.id) {
          const loadReportOptions = await this.getReportOptions(this.selectedReportOptions.id)
          const { filter: { reportOptions, selectedCodes, transactionTypes } } = loadReportOptions
          if (!this.accounts.some(acct => acct.is_summarized) && reportOptions.summarizeLines) {
            reportOptions.summarizeLines = false
          }
          this.reportOptions = reportOptions
          setTimeout(() => {
            this.selectedCodes = {
              transaction_numbers: '',
              ledger_id_in: '',
              ledger_id_btw: '',
              ...selectedCodes
            }
            Object.keys(this.transactionTypes).forEach(key => {
              if (transactionTypes[key]) {
                this.transactionTypes[key].include = transactionTypes[key].include
              } else {
                this.transactionTypes[key].include = false
              }
            })
            this.transactionTypes = { ...this.transactionTypes, ...(transactionTypes || {}) }
            this.reRender++
            if (reportOptions.accountQuickPick) {
              if (reportOptions.accountQuickPick === 'range') this.checkCodeValidity()
              else this.updateAccounts(reportOptions.accountQuickPick)
            }
          }, 10)
        } else {
          this.empty_error = true
          this.visible = true
        }
      },
      forceFullExpand (api, attemptsLeft = 6) {
        api.forEachNode((node) => {
          if (node.group && !node.expanded) {
            node.setExpanded(true)
          }
        })
        if (attemptsLeft <= 0) return

        let done = false
        const rerun = () => {
          if (done) return
          done = true
          api.removeEventListener('modelUpdated', rerun)
          this.forceFullExpand(api, attemptsLeft - 1)
        }
        api.addEventListener('modelUpdated', rerun)
        setTimeout(rerun, 150)
      },
      // forceFullExpand's bounded retry gave up, which is why they read as collapsed.
      autoExpandLoadedGroups (api) {
        if (!api || this.displayOptions.groupingLevel === 1) return   // Collapse All in effect
        if (this.isAutoExpanding) return                             // setExpanded re-enters here
        this.isAutoExpanding = true
        try {
          api.forEachNode((node) => {
            if (node.group && !node.expanded && this.collapsedGroupRoutes.indexOf(this.getGroupRoute(node)) === -1) {
              node.setExpanded(true)
            }
          })
        } finally {
          this.isAutoExpanding = false
        }
      },
      // closed. Dispatched synchronously by setExpanded, so it always wins the race.
      rememberGroupExpansion (node) {
        if (!node || !node.group) return
        const index = this.collapsedGroupRoutes.indexOf(this.getGroupRoute(node))
        if (node.expanded) {
          if (index > -1) this.collapsedGroupRoutes.splice(index, 1)
        } else if (index === -1) {
          this.collapsedGroupRoutes.push(this.getGroupRoute(node))
        }
      },
      async deleteOptions () {
        try {
          if (this.selectedReportOptions) {
            this.visible_delete = true
          } else {
            this.delete_error = true
            this.visible = true
          }
        } catch (e) {
          console.log(e)
        }
      },
      async confirmDeleteOptions () {
        try {
          await this.deleteReportOptions(this.selectedReportOptions.id)
          this.savedReportOptions = this.savedReportOptions.filter(options => options.id !== this.selectedReportOptions.id)
          this.selectedReportOptions = null
          this.selectedOption = null
          this.resetFilters()
          this.getOptions()
          this.$notify({ title: 'Success', message: 'Report Options Deleted', type: 'success' })
        } catch (e) {
          console.log(e)
        }
        this.visible_delete = false
      },
      handleSelection (value) {
        this.selectedReportOptions = value ? this.savedReportOptions.find((s) => s.id === value) : null
        if (!value) this.resetFilters()
      },
      resetFilters () {
        this.reportOptions = {
          transactionStatuses: [ 'posted', 'closed' ],
          accountQuickPick: 'all',
          vendorQuickPick: 'all',
          type: 'detail',
          dates: 'datesAll',
          sort: 'account',
          status: 'all',
          format: 'pdf',
          currencyFactor: undefined,
          report_currency_id: 1,
          includeunposted: false,
          summarizeLines: false
        }
        this.selectedCodes = {
          account_id1: '',
          account_id2: '',
          period_start: undefined,
          period_end: undefined,
          time_posted_start: undefined,
          time_posted_end: undefined,
          effective_date_start: undefined,
          effective_date_end: undefined,
          account_id: [],
          series_id: [],
          location_id: [],
          set_id: [],
          free_field_1_id: [],
          free_field_2_id: [],
          free_field_3_id: [],
          free_field_4_id: [],
          company_id: undefined,
          production_id: [],
          source_code_id: [],
          asset: undefined,
          currency_id: [],
          vendor_id: [],
          transaction_numbers: '',
          ledger_id_in: '',
          ledger_id_btw: ''
        }
        this.transactionTypes = {     // Types of transactions and whether we need them or not
          'aps': { name: 'AP', include: true },
          'jes': { name: 'JE', include: true },
          'pcs': { name: 'PC', include: true },
          'prs': { name: 'PR', include: true },
          'cks': { name: 'CK', include: true },
          'pos': { name: 'PO', include: false },
          'efts': { name: 'EFT', include: true },
          'wts': { name: 'WIRE', include: true },
          'mcs': { name: 'MC', include: true },
          'achs': { name: 'ACH', include: true },
          'dds': { name: 'DD', include: true },
          'nas': { name: 'NA', include: true }
        }
        this.summarization = { value: false, type: 'one_line' }
      },
      // Grid Methods
      onGridReady (params) {
        if (params) {
          this.gridApi = params.api
          this.columnApi = params.columnApi
          this.gridApi.showLoadingOverlay()
          // Restore grid state after grid is ready
          // this._expandRunToken = 0          // NEW — reset per fresh grid instance
          this.restoreGridState()
        }
      },
      onFirstDataRendered (params) {
        this.isLoading = false
        this.loading = false
        // Reliable default-expand under SSRM's partial store — see note above
        // about why isServerSideGroupOpenByDefault can't be trusted here.
        params.api.hideOverlay()
        // params.api.expandAll()
        if (this.isGroupedByAccount) {
          this.forceFullExpand(params.api)   // was: params.api.expandAll()
        }
        // this._expandRunToken = (this._expandRunToken || 0) + 1
        // const myToken = this._expandRunToken
        // if (myToken > 1) return

        if (this.filterState && this.gridApi) {
          this.gridApi.setFilterModel(this.filterState)
        }
      },
      saveFilterState () {
        if (!this.gridApi) return
        this.filterState = this.gridApi.getFilterModel()
      },
      // Restore the saved Group by Account preference onto displayOptions
      applySavedDisplayOptions () {
        const savedState = this.userGridState
        if (!savedState || typeof savedState.groupByAccount !== 'boolean') return
        this.displayOptions.groupByAccount = savedState.groupByAccount
        this.displayOptions.groupingLevel = this.isGroupedByAccount ? 2 : 1
      },
      saveGridState () {
        if (!this.gridApi || !this.columnApi) return

        const columnState = this.columnApi.getColumnState()
        const columnOrder = this.columnApi.getAllGridColumns().map((col) => col.getColId())
        const groupByAccount = !!this.displayOptions.groupByAccount
        const state = { columnState, columnOrder, groupByAccount }

        try {
          this.userGridState = state
          const localGridState = JSON.parse(localStorage.getItem('glReportGridState')) || []
          // get grid state for all users except current user
          const allUsersGridState = localGridState.filter(v => v.userId !== this.$store.getters.getUser.userId)
          // Add current user's grid state to the array
          allUsersGridState.push({
            userId: this.$store.getters.getUser.userId,
            gridState: state
          })
          // Set local storage with the new grid state for all users
          localStorage.setItem('glReportGridState', JSON.stringify(allUsersGridState))
        } catch (error) {
          console.warn('Failed to save grid state:', error)
        }
      },
      restoreGridState () {
        if (!this.gridApi || !this.columnApi) return
        // Per grid instance: a sort held over from the previous build belongs to
        // a column state that is about to be re-read.
        this.pendingSortState = null

        try {
          const savedState = this.userGridState
          if (!savedState) {
            if (this.tab === 'report') {
              this.gridApi.sizeColumnsToFit()
            }
            return
          }

          const state = savedState

          if (state.columnState) {
            // Reorder the state to match the saved order before applying. Grouping and the mode-driven columns' visibility belong to buildWorksheetColumnDefs;
            // sort is replayed later by applyPendingSortState, since applying it here
            // would throw inside ag-grid's SSRM sort listener (no datasource yet).
            const modeDrivenColumns = ['reportGroup', 'groupAccount', 'trans_number']
            const pendingSort = []
            let applyState = state.columnState.map((col) => {
              const { aggFunc, rowGroup, rowGroupIndex, sort, sortIndex, ...rest } = col
              if (modeDrivenColumns.includes(col.colId)) delete rest.hide
              // Grouped mode is not sortable, so a sort saved while flat is dropped.
              if (sort && !this.isGroupedByAccount) pendingSort.push({ colId: col.colId, sort, sortIndex })
              return rest
            })
            this.pendingSortState = pendingSort.length ? pendingSort : null
            if (Array.isArray(state.columnOrder) && state.columnOrder.length) {
              const orderedState = state.columnOrder.map((colId) => applyState.find((c) => c.colId === colId)).filter(Boolean)
              if (orderedState.length) applyState = orderedState
            }
            // Apply column state with order if available, otherwise apply as it is
            if (typeof this.columnApi.applyColumnState === 'function') {
              this.columnApi.applyColumnState({ state: applyState, applyOrder: true })
            } else {
              this.columnApi.setColumnState(applyState)
            }
            // Update column visibility based on saved state for display options
            this.displayOptions.columns.forEach(col => {
              const colState = state.columnState.find(c => c.colId === col.name)
              if (colState) {
                col.display = !colState.hide
              }
            })
          }
        } catch (error) {
          console.warn('Failed to restore grid state:', error)
        }
      },
      async onCellValueChanged (params) {
        // Ignore if not changed
        if ((params.oldValue === params.newValue) || (params.oldValue === null && params.newValue === undefined)) return

        let transactionType = params.data.trans_type
        let transactionTypeLower = transactionType.toLowerCase()
        if (transactionTypeLower === 'pr') transactionTypeLower = 'je'

        if (params.colDef.colId) {
          let unPostedTransactionIndex = this.unPostedTransactionsToBeUpdated.findIndex(trn => Number(trn.id) === Number(params.data.subledger_id) && trn.type === transactionTypeLower && this.unpostedStatuses.includes(params.data.transaction_status))
          let unPostedLineIndex = -1
          if (unPostedTransactionIndex > -1) {
            unPostedLineIndex = this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.findIndex(v => Number(v.id) === Number(params.data.line_id))
          }
          let jeLineIndex = this.jeLinesToBeUpdated.findIndex(v => v.id === params.data.line_id && Number(v.dcLineId) === Number(params.data.dc_line_id))
          let poLineIndex = this.poLinesToBeUpdated.findIndex(v => v.id === params.data.line_id && Number(v.dcLineId) === Number(params.data.dc_line_id))
          const rowKey = this.getStableRowKey(params.data)   // NEW — declared once, used by every case below
          switch (params.colDef.colId) {
            case 'account':
              let account = this.allCodes.accounts.find(v => v.code === params.newValue && v.postable)
              if (!account) {
                this.markFieldError(rowKey, 'account', true)
                params.api.refreshCells({ rowNodes: [params.node], columns: ['account'], force: true })
                this.$notify({ title: 'Error', message: 'Invalid account', type: 'error' })
                return
              }
              this.markFieldError(rowKey, 'account', false)
              params.api.refreshCells({ rowNodes: [params.node], columns: ['account'], force: true })
              if (unPostedLineIndex > -1) {
                this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data[unPostedLineIndex]['account'] = account.id
              } else if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['account'] = account.id
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['account'] = account.id
              } else {
                if (this.unpostedStatuses.includes(params.data.transaction_status)) {
                  if (unPostedTransactionIndex > -1) {
                    this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.push({
                      id: params.data.line_id, account: account.id
                    })
                  } else {
                    this.unPostedTransactionsToBeUpdated.push({
                      id: params.data.subledger_id,
                      type: transactionTypeLower,
                      data: [{ id: params.data.line_id, account: account.id }]
                    })
                  }
                } else {
                  const item = {
                    id: params.data.line_id,
                    account: account.id,
                    dcLineId: Number(params.data.dc_line_id),
                    trnType: params.data.trans_type,
                    trnId: params.data.subledger_id,
                    trnLedgerId: params.data.transaction_ledger_id
                  }
                  if (transactionType === 'PO') {
                    this.poLinesToBeUpdated.push(item)
                  } else {
                    this.jeLinesToBeUpdated.push(item)
                  }
                }
              }
              break
            case 'set':
              let set = this.allCodes.sets.find(v => v.code === params.newValue)
              if (!set) {
                this.markFieldError(rowKey, 'set', true)
                params.api.refreshCells({ rowNodes: [params.node], columns: ['set'], force: true })
                this.$notify({ title: 'Error', message: 'Invalid set', type: 'error' })
                return
              }
              this.markFieldError(rowKey, 'set', false)
              params.api.refreshCells({ rowNodes: [params.node], columns: ['set'], force: true })
              if (unPostedLineIndex > -1) {
                this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data[unPostedLineIndex]['set'] = set.id
              } else if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['set'] = set.id
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['set'] = set.id
              } else {
                if (this.unpostedStatuses.includes(params.data.transaction_status)) {
                  if (unPostedTransactionIndex > -1) {
                    this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.push({
                      id: params.data.line_id, set: set.id
                    })
                  } else {
                    this.unPostedTransactionsToBeUpdated.push({
                      id: params.data.subledger_id,
                      type: transactionTypeLower,
                      data: [{ id: params.data.line_id, set: set.id }]
                    })
                  }
                } else {
                  const item = {
                    id: params.data.line_id,
                    set: set.id,
                    dcLineId: Number(params.data.dc_line_id),
                    trnType: params.data.trans_type,
                    trnId: params.data.subledger_id,
                    trnLedgerId: params.data.transaction_ledger_id
                  }
                  if (transactionType === 'PO') {
                    this.poLinesToBeUpdated.push(item)
                  } else {
                    this.jeLinesToBeUpdated.push(item)
                  }
                }
              }
              break
            case 'location':
              let location = this.allCodes.locations.find(v => v.code === params.newValue)
              if (!location) {
                this.markFieldError(rowKey, 'location', true)
                params.api.refreshCells({ rowNodes: [params.node], columns: ['location'], force: true })
                this.$notify({ title: 'Error', message: 'Invalid location', type: 'error' })
                return
              }
              this.markFieldError(rowKey, 'location', false)
              params.api.refreshCells({ rowNodes: [params.node], columns: ['location'], force: true })
              if (unPostedLineIndex > -1) {
                this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data[unPostedLineIndex]['location'] = location.id
              } else if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['location'] = location.id
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['location'] = location.id
              } else {
                if (this.unpostedStatuses.includes(params.data.transaction_status)) {
                  if (unPostedTransactionIndex > -1) {
                    this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.push({
                      id: params.data.line_id, location: location.id
                    })
                  } else {
                    this.unPostedTransactionsToBeUpdated.push({
                      id: params.data.subledger_id,
                      type: transactionTypeLower,
                      data: [{ id: params.data.line_id, location: location.id }]
                    })
                  }
                } else {
                  const item = {
                    id: params.data.line_id,
                    location: location.id,
                    dcLineId: Number(params.data.dc_line_id),
                    trnType: params.data.trans_type,
                    trnId: params.data.subledger_id,
                    trnLedgerId: params.data.transaction_ledger_id
                  }
                  if (transactionType === 'PO') {
                    this.poLinesToBeUpdated.push(item)
                  } else {
                    this.jeLinesToBeUpdated.push(item)
                  }
                }
              }
              break
            case 'series':
              let series = this.allCodes.series.find(v => v.code === params.newValue)
              if (!series) {
                this.markFieldError(rowKey, 'series', true)
                params.api.refreshCells({ rowNodes: [params.node], columns: ['series'], force: true })
                this.$notify({ title: 'Error', message: 'Invalid series', type: 'error' })
                return
              }
              this.markFieldError(rowKey, 'series', false)
              params.api.refreshCells({ rowNodes: [params.node], columns: ['series'], force: true })
              if (unPostedLineIndex > -1) {
                this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data[unPostedLineIndex]['series'] = series.id
              } else if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['series'] = series.id
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['series'] = series.id
              } else {
                if (this.unpostedStatuses.includes(params.data.transaction_status)) {
                  if (unPostedTransactionIndex > -1) {
                    this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.push({
                      id: params.data.line_id, series: series.id
                    })
                  } else {
                    this.unPostedTransactionsToBeUpdated.push({
                      id: params.data.subledger_id,
                      type: transactionTypeLower,
                      data: [{ id: params.data.line_id, series: series.id }]
                    })
                  }
                } else {
                  const item = {
                    id: params.data.line_id,
                    series: series.id,
                    dcLineId: Number(params.data.dc_line_id),
                    trnType: params.data.trans_type,
                    trnId: params.data.subledger_id,
                    trnLedgerId: params.data.transaction_ledger_id
                  }
                  if (transactionType === 'PO') {
                    this.poLinesToBeUpdated.push(item)
                  } else {
                    this.jeLinesToBeUpdated.push(item)
                  }
                }
              }
              break
            case 'tax_code':
              let taxcode = null
              if (params.newValue.trim()) {
                taxcode = this.allCodes.taxcodes.find(v => v.code === params.newValue)
                if (!taxcode) {
                  this.markFieldError(rowKey, 'tax_code', true)
                  params.api.refreshCells({ rowNodes: [params.node], columns: ['tax_code'], force: true })
                  this.$notify({ title: 'Error', message: 'Invalid tax code', type: 'error' })
                  return
                } else {
                  taxcode = taxcode.id
                }
              }
              // Clearing the field to blank is valid (not an error) — this line
              // covers both "cleared" and "validly matched" outcomes.
              this.markFieldError(rowKey, 'tax_code', false)
              params.api.refreshCells({ rowNodes: [params.node], columns: ['tax_code'], force: true })
              if (unPostedLineIndex > -1) {
                this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data[unPostedLineIndex]['tax_code'] = taxcode
              } else if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['tax_code'] = taxcode
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['tax_code'] = taxcode
              } else {
                if (this.unpostedStatuses.includes(params.data.transaction_status)) {
                  if (unPostedTransactionIndex > -1) {
                    this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.push({
                      id: params.data.line_id, tax_code: taxcode
                    })
                  } else {
                    this.unPostedTransactionsToBeUpdated.push({
                      id: params.data.subledger_id,
                      type: transactionTypeLower,
                      data: [{ id: params.data.line_id, tax_code: taxcode }]
                    })
                  }
                } else {
                  const item = {
                    id: params.data.line_id,
                    tax_code: taxcode,
                    dcLineId: Number(params.data.dc_line_id),
                    trnType: params.data.trans_type,
                    trnId: params.data.subledger_id,
                    trnLedgerId: params.data.transaction_ledger_id
                  }
                  if (transactionType === 'PO') {
                    this.poLinesToBeUpdated.push(item)
                  } else {
                    this.jeLinesToBeUpdated.push(item)
                  }
                }
              }
              break
            case 'free_field_1':
            case 'free_field_2':
            case 'free_field_3':
            case 'free_field_4':
              let freeField = null
              if (params.newValue.trim()) {
                freeField = this.allCodes.freefield1.find(v => v.code === params.newValue)
                if (!freeField) {
                  this.markFieldError(rowKey, params.colDef.colId, true)
                  params.api.refreshCells({ rowNodes: [params.node], columns: [params.colDef.colId], force: true })
                  this.$notify({ title: 'Error', message: 'Invalid Free field', type: 'error' })
                  return
                } else {
                  freeField = freeField.id
                }
              }
              this.markFieldError(rowKey, params.colDef.colId, false)
              params.api.refreshCells({ rowNodes: [params.node], columns: [params.colDef.colId], force: true })
              if (unPostedLineIndex > -1) {
                this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data[unPostedLineIndex][params.colDef.colId] = freeField
              } else if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex][params.colDef.colId] = freeField
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex][params.colDef.colId] = freeField
              } else {
                if (this.unpostedStatuses.includes(params.data.transaction_status)) {
                  let openLineItem = { id: params.data.line_id }
                  openLineItem[params.colDef.colId] = freeField
                  if (unPostedTransactionIndex > -1) {
                    this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.push(openLineItem)
                  } else {
                    this.unPostedTransactionsToBeUpdated.push({
                      id: params.data.subledger_id,
                      type: transactionTypeLower,
                      data: [openLineItem]
                    })
                  }
                } else {
                  let item = {
                    id: params.data.line_id,
                    dcLineId: Number(params.data.dc_line_id),
                    trnType: params.data.trans_type,
                    trnId: params.data.subledger_id,
                    trnLedgerId: params.data.transaction_ledger_id
                  }
                  item[params.colDef.colId] = freeField
                  if (transactionType === 'PO') {
                    this.poLinesToBeUpdated.push(item)
                  } else {
                    this.jeLinesToBeUpdated.push(item)
                  }
                }
              }
              break
            case 'line_desc':
              if (unPostedLineIndex > -1) {
                this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data[unPostedLineIndex]['description'] = params.newValue
              } else if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['description'] = params.newValue
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['description'] = params.newValue
              } else {
                if (this.unpostedStatuses.includes(params.data.transaction_status)) {
                  if (unPostedTransactionIndex > -1) {
                    this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.push({
                      id: params.data.line_id, description: params.newValue
                    })
                  } else {
                    this.unPostedTransactionsToBeUpdated.push({
                      id: params.data.subledger_id,
                      type: transactionTypeLower,
                      data: [{ id: params.data.line_id, description: params.newValue }]
                    })
                  }
                } else {
                  const item = {
                    id: params.data.line_id,
                    description: params.newValue,
                    dcLineId: Number(params.data.dc_line_id),
                    trnType: params.data.trans_type,
                    trnId: params.data.subledger_id,
                    trnLedgerId: params.data.transaction_ledger_id
                  }
                  if (transactionType === 'PO') {
                    this.poLinesToBeUpdated.push(item)
                  } else {
                    this.jeLinesToBeUpdated.push(item)
                  }
                }
              }
              break
            case 'asset':
              if (unPostedLineIndex > -1) {
                this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data[unPostedLineIndex]['asset'] = params.newValue === 'X'
              } else if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['asset'] = params.newValue === 'X'
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['asset'] = params.newValue === 'X'
              } else {
                if (this.unpostedStatuses.includes(params.data.transaction_status)) {
                  if (unPostedTransactionIndex > -1) {
                    this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.push({
                      id: params.data.line_id, asset: params.newValue === 'X'
                    })
                  } else {
                    this.unPostedTransactionsToBeUpdated.push({
                      id: params.data.subledger_id,
                      type: transactionTypeLower,
                      data: [{ id: params.data.line_id, asset: params.newValue === 'X' }]
                    })
                  }
                } else {
                  const item = {
                    id: params.data.line_id,
                    asset: params.newValue === 'X',
                    dcLineId: Number(params.data.dc_line_id),
                    trnType: params.data.trans_type,
                    trnId: params.data.subledger_id,
                    trnLedgerId: params.data.transaction_ledger_id
                  }
                  if (transactionType === 'PO') {
                    this.poLinesToBeUpdated.push(item)
                  } else {
                    this.jeLinesToBeUpdated.push(item)
                  }
                }
              }
              break
            case 'doc_number':
              if (unPostedLineIndex > -1) {
                this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data[unPostedLineIndex]['doc_number'] = params.newValue
              } else if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['doc_number'] = params.newValue
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['doc_number'] = params.newValue
              } else {
                if (this.unpostedStatuses.includes(params.data.transaction_status)) {
                  if (unPostedTransactionIndex > -1) {
                    this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.push({
                      id: params.data.line_id, doc_number: params.newValue
                    })
                  } else {
                    this.unPostedTransactionsToBeUpdated.push({
                      id: params.data.subledger_id,
                      type: transactionTypeLower,
                      data: [{ id: params.data.line_id, doc_number: params.newValue }]
                    })
                  }
                } else {
                  const item = {
                    id: params.data.line_id,
                    doc_number: params.newValue,
                    dcLineId: Number(params.data.dc_line_id),
                    trnType: params.data.trans_type,
                    trnId: params.data.subledger_id,
                    trnLedgerId: params.data.transaction_ledger_id
                  }
                  if (transactionType === 'PO') {
                    this.poLinesToBeUpdated.push(item)
                  } else {
                    this.jeLinesToBeUpdated.push(item)
                  }
                }
              }
              break
            default:
              if (unPostedLineIndex > -1) {
                this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data[unPostedLineIndex][params.colDef.colId] = params.newValue
              } else if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex][params.colDef.colId] = params.newValue
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex][params.colDef.colId] = params.newValue
              } else {
                if (this.unpostedStatuses.includes(params.data.transaction_status)) {
                  let openLineItem = { id: params.data.line_id }
                  openLineItem[params.colDef.colId] = params.newValue
                  if (unPostedTransactionIndex > -1) {
                    this.unPostedTransactionsToBeUpdated[unPostedTransactionIndex].data.push(openLineItem)
                  } else {
                    this.unPostedTransactionsToBeUpdated.push({
                      id: params.data.subledger_id,
                      type: transactionTypeLower,
                      data: [openLineItem]
                    })
                  }
                } else {
                  let item = {
                    id: params.data.line_id,
                    dcLineId: Number(params.data.dc_line_id),
                    trnType: params.data.trans_type,
                    trnId: params.data.subledger_id,
                    trnLedgerId: params.data.transaction_ledger_id
                  }
                  item[params.colDef.colId] = params.newValue
                  if (transactionType === 'PO') {
                    this.poLinesToBeUpdated.push(item)
                  } else {
                    this.jeLinesToBeUpdated.push(item)
                  }
                }
              }
              break
          }
        }
      },
      buildOffsetLine (newLine, existingLine, lineNumber) {
        let originalLineNumber = existingLine.meta_data && existingLine.meta_data.original_line_number ? existingLine.meta_data.original_line_number : null
        const metaData = {
          original_line: existingLine.line_id,
          original_line_type: this.getJeLineType(existingLine.trans_type),
          original_line_number: originalLineNumber
        }
        return {
          account: newLine.account ? newLine.account : existingLine.account_id,
          amount_decimal: existingLine.amount_native,
          description: newLine.description ? newLine.description : existingLine.line_desc,
          other_name: existingLine.other_name || '',
          location: newLine.location ? newLine.location : existingLine.location_id,
          series: newLine.series ? newLine.series : existingLine.series_id,
          set: newLine.set ? newLine.set : existingLine.set_id,
          tax_code: newLine.hasOwnProperty('tax_code') ? newLine.tax_code : existingLine.tax_code_id,
          free_field_1: newLine.hasOwnProperty('free_field_1') ? newLine.free_field_1 : existingLine.free_field_1_id,
          free_field_2: newLine.hasOwnProperty('free_field_2') ? newLine.free_field_2 : existingLine.free_field_2_id,
          free_field_3: newLine.hasOwnProperty('free_field_3') ? newLine.free_field_3 : existingLine.free_field_3_id,
          free_field_4: newLine.hasOwnProperty('free_field_4') ? newLine.free_field_4 : existingLine.free_field_4_id,
          asset: newLine.asset ? Boolean(newLine.asset) : Boolean(existingLine.asset),
          third_party_vendor: existingLine.third_party_vendor_id || '',
          line_number: lineNumber,
          meta_data: metaData
        }
      },
      buildOffsettingLine (originalLine, lineNumber) {
        const metaData = {
          original_line: originalLine.line_id,
          original_line_type: this.getJeLineType(originalLine.trans_type),
          offset_attempt: true
        }
        return {
          account: originalLine.account_id,
          amount_decimal: (originalLine.amount_native * -1).toString(),
          description: originalLine.line_desc,
          other_name: originalLine.other_name || '',
          location: originalLine.location_id ? originalLine.location_id : this.allCodes.locations.find(v => v.active).id,
          series: originalLine.series_id ? originalLine.series_id : this.allCodes.series.find(v => v.active).id,
          set: originalLine.set_id ? originalLine.set_id : this.allCodes.sets.find(v => v.active).id,
          tax_code: originalLine.tax_code_id,
          free_field_1: originalLine.free_field_1_id,
          free_field_2: originalLine.free_field_2_id,
          free_field_3: originalLine.free_field_3_id,
          free_field_4: originalLine.free_field_4_id,
          asset: Boolean(originalLine.asset),
          third_party_vendor: originalLine.third_party_vendor_id || '',
          line_number: lineNumber,
          meta_data: metaData
        }
      },
      buildExistingOffsetLine (line, lineNumber) {
        return {
          account: line.account,
          amount_decimal: line.amount_decimal,
          description: line.description,
          other_name: line.other_name || '',
          location: line.location,
          series: line.series,
          set: line.set,
          tax_code: line.tax_code,
          free_field_1: line.free_field_1,
          free_field_2: line.free_field_2,
          free_field_3: line.free_field_3,
          free_field_4: line.free_field_4,
          asset: Boolean(line.asset),
          third_party_vendor: line.third_party_vendor || '',
          line_number: lineNumber,
          meta_data: line.meta_data
        }
      },
      getJeLineType (trnType) {
        let lineType = ''
        switch (trnType) {
          case 'JE':
          case 'PR':
            lineType = 'journalentryline'
            break
          case 'AP':
            lineType = 'accountspayableline'
            break
          case 'PC':
            lineType = 'pettycashline'
            break
          case 'PO':
            lineType = 'purchaseorderline'
            break
        }
        return lineType
      },
      buildJeHeader (existingJe) {
        return {
          batch: existingJe.batch,
          period: existingJe.period || null,
          description: existingJe.description,
          number: existingJe.number,
          debits_decimal: existingJe.debits_decimal,
          credits_decimal: existingJe.credits_decimal,
          original_trx_number: existingJe.original_trx_number,
          effective_date: moment(new Date()).format('YYYY-MM-DD'),
          date: moment(new Date()).format('YYYY-MM-DD'),
          source_code: this.allCodes.sourcecodes.find(v => v.code === 'DC').id || '',
          lines: []
        }
      },
      buildDcJeHeader (offsetJeLines, existingLine) {
        const debits = offsetJeLines.reduce((a, b) => a + (parseFloat(b.amount_decimal) > 0 ? parseFloat(b.amount_decimal) : 0), 0).toFixed(2)
        const credits = offsetJeLines.reduce((a, b) => a + (parseFloat(b.amount_decimal) < 0 ? parseFloat(b.amount_decimal) : 0), 0).toFixed(2)
        return {
          batch: existingLine.transaction_batch_id,
          period: existingLine.transaction_period_id || null,
          description: `DC for ${existingLine.transaction_ledger_id}`,
          number: existingLine.doc_number,
          debits_decimal: debits,
          credits_decimal: credits,
          original_trx_number: existingLine.transaction_ledger_id,
          effective_date: moment(new Date()).format('YYYY-MM-DD'),
          date: moment(new Date()).format('YYYY-MM-DD'),
          source_code: this.allCodes.sourcecodes.find(v => v.code === 'DC').id || '',
          lines: []
        }
      },
      isExternalFilterPresent () {
        return this.$data.displayOptions.hideZeroRows
      },
      doesExternalFilterPass (node) {
        if (!node || !node.data) {
          return false
        } else {
          return true
        }
      },
      onExport () {
        this.gridApi.exportDataAsExcel()
      },
      onChangeVisibleColumns (column = null) {
        if (column) {
          this.$nextTick(() => {
            // update column visibility based on changed state in displayOptions columns
            this.columnApi.setColumnVisible(column, this.displayOptions.columns.find((v) => { return v.name === column }).display)
            // Save state after column visibility change
            this.saveGridState()
          })
        } else {
          this.$nextTick(() => {
            // Update column visibility based on changed state of column(s)
            const state = this.columnApi.getColumnState()
            this.displayOptions.columns.forEach(col => {
              const colState = state.find(c => c.colId === col.name)
              if (colState) {
                col.display = !colState.hide
              }
            })
            // Save state after column visibility changes
            this.saveGridState()
          })
        }
      },
      printPdf (type) {
        this.isPrinting = true
        if (this.jeLinesToBeUpdated.length || this.poLinesToBeUpdated.length || this.unPostedTransactionsToBeUpdated.length) {
          this.$notify({ title: 'Error', message: 'Please save your changes before printing.', type: 'error' })
          this.isPrinting = false
          return
        }
        this.isPrinted = true
        this.reportOptions.type = type
        this.jasperSettings.reportName = 'general_ledger_' + type
        this.summarization.value = this.reportOptions.summarizeLines
        this.getGlReportData()
          .then((resSorted) => {
            return this.buildGlReport(resSorted)
          })
          .then(json => {
            if (this.showJson) this.prettyJson = JSON.stringify(json, null, 2)
            return this.getJsonUrl(this.buildJasperPayload(json))
          })
          .then(resJsonUrl => {
            let report = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.pdf?net.sf.jasperreports.json.source=' + resJsonUrl
            return this.getReportUrl(report)
          })
          .then(reportUrl => {
            this.pdfData = reportUrl
            this.tab = 'viewer'
            this.isPrinting = false
          })
      },
      async jesToBeCreated () {
        let jes = []
        // VX source code id
        const voidSourceCode = this.allCodes.sourcecodes.find(v => v.code === 'VX').id || ''
        // DC source code id
        const dcSourceCode = this.allCodes.sourcecodes.find(v => v.code === 'DC').id || ''
        // Unique transactions of updated lines
        const trns = [...new Set(this.jeLinesToBeUpdated.map((item) => item.trnLedgerId))]
        // line ids of updated lines
        const editedLineIds = this.jeLinesToBeUpdated.map((item) => item.id)
        // Original lines to be parsed
        const originalTrnLines = JSON.parse(this.originalLines)

        // Loop each transactions. Find Jes if already created else create Je
        for await (const trn of trns) {
          let je = { header: {}, lines: [] }
          let dcUpdatedDate = ''
          // Get existing Je
          const dcJes = await this.$store.dispatch('fetchJesWithQuery', { source_code: dcSourceCode, original_trx_number: trn, status__in: ['POSTED', 'CLOSED'] })
          if (dcJes && dcJes.results.length) {
            let updatedLines = []
            const dcJesData = dcJes.results.sort((a, b) => a.id - b.id)
            for await (const dcJe of dcJesData) {
              if (dcUpdatedDate) {
                if (new Date(dcUpdatedDate) <= new Date(dcJe.updated)) {
                  dcUpdatedDate = dcJe.updated
                  je.header = this.buildJeHeader(dcJe)
                }
              } else {
                dcUpdatedDate = dcJe.updated
                je.header = this.buildJeHeader(dcJe)
              }
              // Get Je lines
              const results = await this.$store.dispatch('fetchLinesWithQuery', { type: 'je', query: { transaction__id: dcJe.id } })
              if (results && results.count && Object.values(results.entities.lines)) {
                const dcJeLines = Object.values(results.entities.lines).filter((v) => v.meta_data !== null)
                // Check Je lines are in edited lines
                let dcJeLinesEdited = []
                editedLineIds.forEach((id) => {
                  if (dcJeLines.find((v) => Number(v.meta_data.original_line) === Number(id))) {
                    dcJeLinesEdited.push(dcJeLines.find((v) => Number(v.meta_data.original_line) === Number(id)))
                    updatedLines.push(id)
                  }
                })
                // If any of Je lines edited
                if (dcJeLinesEdited && dcJeLinesEdited.length) {
                  // Filter Offset lines
                  let dcJeLinesEditedOriginal = dcJeLines.filter((v) => !v.offset_by)
                  // Loop Je offset lines
                  dcJeLinesEditedOriginal.forEach((dcJeLine) => {
                    // Find edited line
                    const editedLine = this.jeLinesToBeUpdated.find((v) => v.id === dcJeLine.meta_data.original_line && v.dcLineId === dcJeLine.id)
                    let lineNumber = je.lines.length
                    if (editedLine && editedLine.id) {
                      // Offsetting Line
                      const originalOffsettingLine = dcJeLines.find((v) => v.offset_by === dcJeLine.id)
                      if (dcJeLine.other_name) {
                        originalOffsettingLine.other_name = dcJeLine.other_name
                      }
                      if (originalOffsettingLine && originalOffsettingLine.account) {
                        lineNumber++
                        const offsettingLine = this.buildExistingOffsetLine(originalOffsettingLine, lineNumber)
                        je.lines.push(offsettingLine)
                      }
                      // Offset Line
                      const originalLine = originalTrnLines.find((v) => v.line_id === editedLine.id && Number(v.dc_line_id) === editedLine.dcLineId && v.transaction_ledger_id === editedLine.trnLedgerId && isNonPaymentLine(v))
                      if (originalLine && originalLine.line_id) {
                        lineNumber++
                        const offsetLine = this.buildOffsetLine(editedLine, originalLine, lineNumber)
                        je.lines.push(offsetLine)
                      }
                    } else { // create new Je line with existing Je line
                      lineNumber++
                      // Offset Line
                      const offsetLine = this.buildExistingOffsetLine(dcJeLine, lineNumber)
                      je.lines.push(offsetLine)
                      // Offsetting Line
                      const originalOffsettingLine = dcJeLines.find((v) => v.offset_by === dcJeLine.id)
                      if (originalOffsettingLine && originalOffsettingLine.account) {
                        lineNumber++
                        const offsettingLine = this.buildExistingOffsetLine(originalOffsettingLine, lineNumber)
                        je.lines.push(offsettingLine)
                      }
                    }
                  })

                  // VOID JE Transaction
                  this.$store.dispatch('updateJe', { id: dcJe.id, data: { status: 'VOIDED' } })
                    .then((jeObj) => {
                      // fetch the "voiding" (VX) transactions
                      if (jeObj && jeObj.ledger) {
                        this.$store.dispatch('fetchJesWithQuery', { nest_lines: false, source_code: voidSourceCode, original_trx_number: jeObj.ledger })
                          .then((vxJes) => {
                            if (vxJes && vxJes.results.length) { // Update JE Status to POSTED
                              for (const res of vxJes.results) {
                                this.$store.dispatch('updateJe', { id: res.id, data: { status: 'POSTED' } })
                              }
                            }
                          })
                          .catch(error => {
                            console.log(error)
                          })
                      }
                    })
                    .catch(error => {
                      console.log(error)
                    })
                }
              }
            }
            // Lines are edited but no dcs
            if (updatedLines.length) {
              editedLineIds.forEach((id) => {
                if (!updatedLines.includes(id)) {
                  const trnLine = this.jeLinesToBeUpdated.find((item) => Number(item.id) === Number(id) && item.trnLedgerId === trn)
                  if (trnLine && trnLine.id) {
                    // Create if there is no DC line
                    let lineNumber = je.lines.length
                    const originalLine = originalTrnLines.find((v) => Number(v.line_id) === Number(id) && v.transaction_ledger_id === trnLine.trnLedgerId && isNonPaymentLine(v))
                    if (originalLine && originalLine.account_id) {
                      // Offset Line
                      lineNumber++
                      const offsetLine = this.buildOffsetLine(trnLine, originalLine, lineNumber)
                      je.lines.push(offsetLine)
                      lineNumber++
                      // Offsetting Line
                      const offsettingLine = this.buildOffsettingLine(originalLine, lineNumber)
                      je.lines.push(offsettingLine)
                      updatedLines.push(id)
                    }
                  }
                }
              })
            } else if (je.lines.length === 0) {
              const trnLines = this.jeLinesToBeUpdated.filter((item) => item.trnLedgerId === trn)
              let jeLines = []
              trnLines.forEach((trnLine, tln) => {
                // Create if there is no DC line
                if (!je.lines.filter((v) => { return [trnLine.id].includes(v.meta_data.original_line) }).length) {
                  let lineNumber = jeLines.length
                  const originalLine = originalTrnLines.find((v) => v.line_id === trnLine.id && v.transaction_ledger_id === trnLine.trnLedgerId && isNonPaymentLine(v))
                  if (originalLine && originalLine.account_id) {
                    // Offset Line
                    lineNumber++
                    const offsetLine = this.buildOffsetLine(trnLine, originalLine, lineNumber)
                    jeLines.push(offsetLine)
                    lineNumber++
                    // Offsetting Line
                    const offsettingLine = this.buildOffsettingLine(originalLine, lineNumber)
                    jeLines.push(offsettingLine)
                  }

                  if (trnLines.length === (tln + 1)) {
                    je.header = this.buildDcJeHeader(jeLines, originalLine)
                    je.lines = jeLines
                  }
                }
              })
            }
          } else {
            const trnLines = this.jeLinesToBeUpdated.filter((item) => item.trnLedgerId === trn)
            const allowDC = this.canCreateDc(trnLines)
            if (allowDC) { // Create DC JE
              let jeLines = []
              trnLines.forEach((trnLine, tln) => {
                let lineNumber = jeLines.length
                const originalLine = originalTrnLines.find((v) => v.line_id === trnLine.id && v.transaction_ledger_id === trnLine.trnLedgerId && isNonPaymentLine(v))
                if (originalLine && originalLine.account_id) {
                  // Offset Line
                  lineNumber++
                  const offsetLine = this.buildOffsetLine(trnLine, originalLine, lineNumber)
                  jeLines.push(offsetLine)
                  lineNumber++
                  // Offsetting Line
                  const offsettingLine = this.buildOffsettingLine(originalLine, lineNumber)
                  jeLines.push(offsettingLine)
                }

                if (trnLines.length === (tln + 1)) {
                  je.header = this.buildDcJeHeader(jeLines, originalLine)
                  je.lines = jeLines
                }
              })
            } else { // Update transaction lines
              const trnId = trnLines[0].trnId
              let trnType = (trnLines[0].trnType).toLowerCase()
              if (trnType === 'pr') trnType = 'je'
              let linesToBeUpdated = []
              trnLines.forEach((trnLine, k) => {
                linesToBeUpdated[k] = {}
                let updatedColumns = Object.keys(trnLine).filter(x => !['dcLineId', 'trnType', 'trnId', 'trnLedgerId'].includes(x))
                updatedColumns.forEach(updatedColumn => {
                  linesToBeUpdated[k][updatedColumn] = trnLine[updatedColumn]
                })
                if (trnLines.length === (k + 1)) {
                  this.postedTransactionsToBeUpdated.push({ id: trnId, type: trnType, data: linesToBeUpdated })
                }
              })
            }
          }
          if (je.header && je.lines.length) {
            je.header.debits_decimal = je.lines.reduce((a, b) => a + (parseFloat(b.amount_decimal) > 0 ? parseFloat(b.amount_decimal) : 0), 0).toFixed(2)
            je.header.credits_decimal = je.lines.reduce((a, b) => a + (parseFloat(b.amount_decimal) < 0 ? parseFloat(b.amount_decimal) : 0), 0).toFixed(2)
            jes.push(je)
          }
        }
        return jes
      },
      canCreateDc (lines) {
        // No DC Settings
        if (!this.dcColumns.length && !this.dcAllowedColumns.length) return true
        // No columns with Y from DC Settings
        if (this.dcColumns.length && !this.dcAllowedColumns.length) return false

        return Object.values(lines).filter(v => {
          let updatedColumns = Object.keys(v).filter(x => !['id', 'dcLineId', 'trnType', 'trnId', 'trnLedgerId'].includes(x))
          return updatedColumns.some(updatedColumn => {
            return this.dcAllowedColumns.includes(this.dcAllowedColumnMap[updatedColumn])
          })
        }).length
      },
      preparePoTransactions () {
        return new Promise((resolve, reject) => {
          let poTransactions = []
          this.poLinesToBeUpdated.forEach((poLine) => {
            const existingLine = this.workSheetData.find(v => v.line_id === poLine.id && isNonPaymentLine(v))
            if (!existingLine) return
            const transactionId = existingLine.subledger_id ? existingLine.subledger_id : existingLine.trans_number
            const poTrxIndex = poTransactions.findIndex(v => v.id === transactionId)

            const poLineItem = {
              account: poLine.account || existingLine.account_id,
              amount_decimal: existingLine.amount_native,
              asset: poLine.asset || existingLine.asset,
              description: poLine.description || existingLine.line_desc,
              free_field_1: poLine.free_field_1 || existingLine.free_field_1_id,
              free_field_2: poLine.free_field_2 || existingLine.free_field_2_id,
              free_field_3: poLine.free_field_3 || existingLine.free_field_3_id,
              free_field_4: poLine.free_field_4 || existingLine.free_field_4_id,
              id: poLine.id || existingLine.line_id,
              line_number: existingLine.line_number,
              location: poLine.location || existingLine.location_id,
              other_name: existingLine.other_name || '',
              series: poLine.series || existingLine.series_id,
              set: poLine.set || existingLine.set_id,
              tax_code: poLine.tax_code || existingLine.tax_code_id,
              third_party_vendor: poLine.third_party_vendor || existingLine.third_party_vendor_id,
              transaction: transactionId,
              type: existingLine.line_type
            }
            if (poTrxIndex > -1) {
              poTransactions[poTrxIndex].data.lines.push(poLineItem)
            } else {
              poTransactions.push({
                id: transactionId,
                data: {
                  lines: [poLineItem]
                }
              })
            }
          })
          resolve(poTransactions)
        })
      },
      async saveChanges (e) {
        this.isSaving = true
        if (!this.jeLinesToBeUpdated.length && !this.poLinesToBeUpdated.length && !this.unPostedTransactionsToBeUpdated.length) {
          this.$notify({ title: 'Warning', message: 'No changes to save', type: 'warning' })
          this.isSaving = false
          return
        }
        // Captured before the pending arrays are filtered down below.
        const accountChanges = new Map()
        this.jeLinesToBeUpdated.concat(this.poLinesToBeUpdated).forEach((item) => {
          if (item.account) accountChanges.set(Number(item.id), item.account)
        })
        const savedLineIds = new Set()
        const failedLines = new Map()   // line_id -> message
        try {
          // Update PO transaction lines
          if (this.poLinesToBeUpdated.length) {
            let poTransactions = await this.preparePoTransactions()
            await Promise.all(poTransactions.map(async poTransaction => {
              await this.$store.dispatch('updatePo', poTransaction)
            }))
            // PO lines update in place, so no DC line to re-point.
            this.poLinesToBeUpdated.forEach((item) => {
              savedLineIds.add(Number(item.id))
              this.patchWorksheetLine(Number(item.id), { edited: false, error: false, tooltip: '' })
            })
          }

          // Update transaction lines in bulk, No DCs
          if (this.unPostedTransactionsToBeUpdated.length) {
            await Promise.all(this.unPostedTransactionsToBeUpdated.map(async transaction => {
              await this.$store.dispatch('updateTransactionLine', transaction)
                .then((response) => {
                  transaction.data.forEach((line) => {
                    if (line.account) accountChanges.set(Number(line.id), line.account)
                  })
                  if (response && response.length) {
                    response.forEach((line) => {
                      savedLineIds.add(Number(line.id))
                      this.patchWorksheetLine(Number(line.id), { edited: false, error: false, tooltip: '' })
                    })
                  } else {
                    let errMessage = 'Failed to save changes'
                    if (response.data && typeof response.data[0] === 'string') {
                      errMessage = response.data[0].replaceAll('{', '').replaceAll('}', '').replaceAll('"', '')
                    }
                    transaction.data.forEach((line) => failedLines.set(line.id, errMessage))
                  }
                })
            }))
          }

          // Update transaction lines by creating distribution line changes
          if (this.jeLinesToBeUpdated.length) {
            let transactions = await this.jesToBeCreated()
            let originalTrnLines = JSON.parse(this.originalLines)

            // Update transaction lines in bulk when there is no DC
            if (this.postedTransactionsToBeUpdated.length) {
              await Promise.all(this.postedTransactionsToBeUpdated.map(async transaction => {
                await this.$store.dispatch('updateTransactionLine', transaction)
                  .then((response) => {
                    if (response && response.length) {
                      response.forEach((line) => {
                        savedLineIds.add(Number(line.id))
                        this.patchWorksheetLine(Number(line.id), { edited: false, error: false, tooltip: '' })
                      })
                    } else {
                      let errMessage = 'Failed to save changes'
                      if (response.data && typeof response.data[0] === 'string') {
                        errMessage = response.data[0].replaceAll('{', '').replaceAll('}', '').replaceAll('"', '')
                      }
                      transaction.data.forEach((line) => failedLines.set(line.id, errMessage))
                    }
                  })
              }))
            }

            await Promise.all(transactions.map(async transaction => {
              const jeHeader = transaction.header
              const lines = transaction.lines
              let originalLineIds = []
              lines.map(line => {
                if (line.meta_data && !line.meta_data.offset_attempt) originalLineIds.push(Number(line.meta_data.original_line))
              })
              // Create new JE DC with offset lines and offsetting lines
              await this.$store.dispatch('saveJe', {...jeHeader, lines})
                .then(async (response) => {
                  if (response && response.id) {
                    response.lines.forEach((line) => {
                      if (line.meta_data && !line.meta_data.offset_attempt && originalLineIds.includes(Number(line.meta_data.original_line))) {
                        const originalLineId = Number(line.meta_data.original_line)
                        // Re-point the worksheet row at the DC line just created, so a
                        // follow-up edit to the same GL line offsets the new DC line.
                        const originalLineIndex = originalTrnLines.findIndex((v) => Number(v.line_id) === originalLineId && isNonPaymentLine(v))
                        if (originalLineIndex > -1) {
                          originalTrnLines[originalLineIndex].dc_line_id = line.id
                          // buildOffsetLine falls back to this snapshot for fields not
                          // edited right now, so it must advance or the next save reverts
                          // whatever this one changed.
                          this.applySavedValuesToSnapshot(originalTrnLines[originalLineIndex], originalLineId)
                        }
                        savedLineIds.add(originalLineId)
                        this.patchWorksheetLine(originalLineId, { dc_line_id: line.id, edited: false, error: false, tooltip: '' })
                      }
                    })
                    await this.$store.dispatch('updateJe', { id: response.id, data: { status: 'POSTED' } })
                  } else {
                    const message = (response && response.data && response.data.message) || 'Failed to save changes'
                    originalLineIds.forEach((id) => failedLines.set(id, message))
                  }
                })
                .catch((error) => {
                  const message = (error && error.response && error.response.data && error.response.data.message) || (error && error.message) || 'Failed to save changes'
                  originalLineIds.forEach((id) => failedLines.set(id, message))
                  console.log('error', error)
                })
            }))
            this.originalLines = JSON.stringify(originalTrnLines)
          }

          // Failed lines keep their edited values and stay queued for a retry.
          failedLines.forEach((message, lineId) => {
            this.patchWorksheetLine(lineId, { error: true, tooltip: message })
          })

          let groupingChanged = false
          const removedLineIds = new Set()
          const reportAccountIds = Array.isArray(this.selectedCodes.account_id) ? this.selectedCodes.account_id : []
          accountChanges.forEach((accountId, lineId) => {
            if (failedLines.has(lineId)) return
            if (!savedLineIds.has(lineId)) {
              console.warn('Account change not re-grouped: line', lineId, 'was not confirmed saved')
              return
            }
            // String compare: a Number() miss here would silently skip the re-group.
            const account = this.allCodes.accounts.find((v) => String(v.id) === String(accountId))
            if (!account) {
              console.warn('Account change not re-grouped: no account for id', accountId, 'on line', lineId)
              return
            }
            // If the report is filtered to a subset of accounts, and the new account is not in that subset, remove the row from the grid.
            if (reportAccountIds.length && !reportAccountIds.some((id) => String(id) === String(account.id))) {
              removedLineIds.add(lineId)
              groupingChanged = true
              return
            }
            const row = this.workSheetData.find((v) => Number(v.line_id) === lineId && isNonPaymentLine(v))
            this.patchWorksheetLine(lineId, {
              account: account.code,
              account_id: account.id,
              account_name: account.name,
              // grandparent/groupAccount place the row in the tree.
              grandparent: account.code,
              groupAccount: account.code,
              // Leaf key only, nothing renders it — re-keyed on line_id so it cannot
              // collide with an existing "<index>__<trans_number>" in the new account.
              code: lineId + '__' + (row ? row.trans_number : '')
            })
            groupingChanged = true
          })

          if (removedLineIds.size) {
            // Freeze the filtered array so the grid does not try to mutate it in place.
            this.workSheetData = Object.freeze(this.workSheetData.filter((row) => !removedLineIds.has(Number(row.line_id))))
          }

          // No worksheet refetch by design (PA-2181): rows are patched in place.
          if (groupingChanged) this.regroupWorksheet()

          if (failedLines.size) {
            this.$notify({ title: 'Warning', message: failedLines.size + ' of ' + (failedLines.size + savedLineIds.size) + ' lines could not be saved. The highlighted rows show why — press Save to retry.', type: 'warning' })
          } else {
            this.$notify({ title: 'Success', message: 'Changes saved successfully', type: 'success' })
          }
          // Keep only failed lines pending so a retry re-sends exactly those.
          this.jeLinesToBeUpdated = this.jeLinesToBeUpdated.filter((item) => failedLines.has(Number(item.id)))
          this.poLinesToBeUpdated = this.poLinesToBeUpdated.filter((item) => failedLines.has(Number(item.id)))
          this.unPostedTransactionsToBeUpdated = this.unPostedTransactionsToBeUpdated.filter(item => item.data.some(line => failedLines.has(Number(line.id))))
          this.postedTransactionsToBeUpdated = []
          this.invalidCellKeys = []
          this.selection = []
          this.closeBulkModal()
          this.isSaving = false
        } catch (error) {
          this.isSaving = false
          console.log(error)
          return false
        }
      },
      patchWorksheetLine (lineId, patch) {
        if (Array.isArray(this.workSheetData)) {
          this.workSheetData.forEach((row) => {
            if (Number(row.line_id) === lineId && isNonPaymentLine(row)) Object.assign(row, patch)
          })
        }
        if (!this.gridApi) return
        const affectedNodes = []
        this.gridApi.forEachNode((node) => {
          if (node.data && Number(node.data.line_id) === lineId && isNonPaymentLine(node.data)) {
            Object.assign(node.data, patch)
            affectedNodes.push(node)
          }
        })
        if (affectedNodes.length) {
          // redrawRows, not refreshCells: refreshCells does not re-evaluate
          // rowClassRules, so the ag-error/ag-changed tint would never update.
          this.gridApi.redrawRows({ rowNodes: affectedNodes })
        }
      },
      // Maps pending-item payload keys to originalLines snapshot field names.
      applySavedValuesToSnapshot (snapshotLine, lineId) {
        const SNAPSHOT_FIELD_BY_PAYLOAD_KEY = {
          account: 'account_id',
          description: 'line_desc',
          location: 'location_id',
          series: 'series_id',
          set: 'set_id',
          tax_code: 'tax_code_id',
          free_field_1: 'free_field_1_id',
          free_field_2: 'free_field_2_id',
          free_field_3: 'free_field_3_id',
          free_field_4: 'free_field_4_id',
          asset: 'asset',
          doc_number: 'doc_number'
        }
        this.jeLinesToBeUpdated
          .filter((item) => Number(item.id) === Number(lineId))
          .forEach((item) => {
            Object.keys(SNAPSHOT_FIELD_BY_PAYLOAD_KEY).forEach((payloadKey) => {
              if (!item.hasOwnProperty(payloadKey)) return
              snapshotLine[SNAPSHOT_FIELD_BY_PAYLOAD_KEY[payloadKey]] = item[payloadKey]
            })
          })
      },
      // Group node identity that survives a store rebuild, e.g. 'GRAND TOTAL/0301'.
      getGroupRoute (node) {
        const parts = []
        let current = node
        while (current && current.key !== null && current.key !== undefined) {
          parts.unshift(current.key)
          current = current.parent
        }
        return parts.join('/')
      },
      // Retries like forceFullExpand: the SSRM loads children lazily, so a
      // level-1 group only exists once its parent has been expanded.
      restoreExpandedGroups (expandedRoutes, attemptsLeft = 6) {
        if (!this.gridApi || !expandedRoutes.size) return
        const api = this.gridApi
        api.forEachNode((node) => {
          if (node.group && !node.expanded && expandedRoutes.has(this.getGroupRoute(node))) {
            node.setExpanded(true)
          }
        })
        if (attemptsLeft <= 0) return

        let done = false
        const rerun = () => {
          if (done) return
          done = true
          api.removeEventListener('modelUpdated', rerun)
          this.restoreExpandedGroups(expandedRoutes, attemptsLeft - 1)
        }
        api.addEventListener('modelUpdated', rerun)
        setTimeout(rerun, 150)
      },
      // Setting a datasource discards the store, and firstDataRendered fires only
      // once per grid, so expanded groups and the filter must be restored here.
      regroupWorksheet () {
        this.saveFilterState()
        const expandedRoutes = new Set()
        let hasCollapsedGroup = false
        if (this.gridApi) {
          this.gridApi.forEachNode((node) => {
            if (!node.group) return
            if (node.expanded) expandedRoutes.add(this.getGroupRoute(node))
            else hasCollapsedGroup = true
          })
        }

        this.localTree = buildLocalTree(this.workSheetData, true)
        this.applyWorksheetDatasource()
        if (!this.gridApi) return

        // Full expand also reveals a group created by this save, which has no
        // previous expanded state to restore.
        if (this.isGroupedByAccount) {
          if (hasCollapsedGroup) {
            this.restoreExpandedGroups(expandedRoutes)
          } else {
            this.forceFullExpand(this.gridApi)
          }
        }
        if (this.filterState && Object.keys(this.filterState).length) {
          this.gridApi.setFilterModel(this.filterState)
        }
        this.updateGrandTotalRow()
      },
      resetChanges () {
        this.jeLinesToBeUpdated = []
        this.poLinesToBeUpdated = []
        this.postedTransactionsToBeUpdated = []
        this.unPostedTransactionsToBeUpdated = []
        this.dcColumns = []
        this.dcAllowedColumns = []
        this.originalLines = ''
        this.invalidCellKeys = []
        this.$notify({ title: 'Success', message: 'Changes saved successfully', type: 'success' })
        this.rowData = null
        this.isSaving = false
        this.selection = []
        this.closeBulkModal()
        this.refreshWorksheet()
      },
      buildTransData (paramsData) {
        // Group bands and the pinned grand-total row have no trans_number.
        if (!paramsData || !paramsData.trans_number) return paramsData ? (paramsData.__groupLabel || '') : ''
        if (paramsData.trans_type === 'PO') {
          return paramsData.trans_number.slice(2)
        }
        return paramsData.trans_number
      },
      async getGridStateSettings () {
        this.userGridState = null
        // Try to get grid state from local storage first
        const localGridState = JSON.parse(localStorage.getItem('glReportGridState')) || []
        let userGrid = null
        if (localGridState.length) {
          userGrid = localGridState.find(v => v.userId === this.$store.getters.getUser.userId)
          if (userGrid) {
            this.userGridState = userGrid.gridState
            this.applySavedDisplayOptions()
          }
        }
        this.$store.dispatch('fetchUiSetting', { name: 'GL_GRID_STATE' })
          .then((response) => {
            if (response && response.value) {
              const gridStateResults = JSON.parse(response.value)
              if (gridStateResults) {
                // Set flag to indicate grid state exists in database
                this.hasGridExists = true
                // Get current user grid state and set to grid
                const currentUserGridState = gridStateResults.find(v => v.userId === this.$store.getters.getUser.userId)
                // Keep all users grid state except current user
                this.allUsersGridState = gridStateResults.filter(v => v.userId !== this.$store.getters.getUser.userId)
                // If no grid state for current user in local storage, get from database
                if (currentUserGridState && !this.userGridState) {
                  this.userGridState = currentUserGridState.gridState
                  this.applySavedDisplayOptions()
                }
              }
            }
          })
          .catch((error) => {
            console.log(error)
          })
        // Saving Instance and headers for later use in saving grid state in database
        this.instance = this.$store.getters.getActiveInstance.url
        this.instanceHeaders = getHeaders(this.$store.getters.getToken)
      },
      async getAppSettings () {
        const entryOptions = this.$store.getters.getUiSettings['TRANSACTION_ENTRY_OPTIONS']
        if (entryOptions && JSON.parse(entryOptions)) {
          this.effectiveDateMandatory = JSON.parse(entryOptions).effective_date
        }
      },
      async getDcSettings () {
        this.dcColumns = []
        this.dcAllowedColumns = []
        let response = await this.$store.dispatch('fetchUiSetting', { name: 'DC_SETTINGS' })
        if (response && response.value) {
          const dcSettings = JSON.parse(response.value)
          this.dcColumns = Object.keys(dcSettings)
          this.dcAllowedColumns = this.dcColumns.filter(x => dcSettings[x] === 'Y')
        }
      },
      toggleBulk () {
        this.bulkform = {
          account: '',
          line_desc: '',
          series: '',
          location: '',
          set: '',
          free_field_1: '',
          free_field_2: '',
          free_field_3: '',
          free_field_4: '',
          tax_code: '',
          asset: ''
        }
        this.bulkSubmitting = false
        this.bulkPopupModal = true
      },
      closeBulkModal () {
        this.bulkSubmitting = false
        this.bulkPopupModal = false
      },
      async submitBulkForm () {
        this.bulkSubmitting = true
        // Group rows carry synthetic data and have no fields to set.
        const selectedChildrenNodes = this.gridApi.getSelectedNodes().filter(rowNode => rowNode.data && !rowNode.data.__isGroup)
        const keysToBeUpdated = Object.keys(this.bulkform).filter((key) => this.bulkform[key] !== '')
        // Only null/undefined/'' are empty — false is a real asset value.
        const isEmptyValue = (value) => value === null || value === undefined || value === ''
        const applied = []
        selectedChildrenNodes.forEach((rowNode) => {
          keysToBeUpdated.forEach((key) => {
            const keyValue = this.bulkform[key] === 'N/A' ? null : this.bulkform[key]
            const currentValue = rowNode.data[key]
            if (currentValue === keyValue || (isEmptyValue(currentValue) && isEmptyValue(keyValue))) return
            if (rowNode && rowNode.data && rowNode.data.recon && key === 'account') return
            rowNode.setDataValue(key, keyValue)
            applied.push({ line_id: rowNode.data.line_id, key, from: currentValue, to: keyValue })
          })
        })
        if (!applied.length) {
          this.$notify({ title: 'Warning', message: 'No changes to apply', type: 'warning' })
          this.closeBulkModal()
          this.bulkSubmitting = false
          return
        }
        // Just a tiny delay to let the grid finish its internal processing before saveChanges() reads the pending arrays.
        await new Promise((resolve) => setTimeout(resolve, 0))

        if (!this.jeLinesToBeUpdated.length && !this.poLinesToBeUpdated.length && !this.unPostedTransactionsToBeUpdated.length) {
          console.warn('Bulk update wrote ' + applied.length + ' cell(s) but queued no changes:', applied)
        }
        await this.saveChanges()
        this.bulkSubmitting = false
      },
      rowSelected (event) {
        const parent = event.node
        if (!parent || !parent.data || !parent.data.__isGroup) return
        const selected = parent.isSelected()
        const selectLoadedChildren = () => {
          this.gridApi.forEachNode((child) => {
            if (child.parent === parent && child.selectable && child.isSelected() !== selected) {
              child.selectThisNode(selected)
            }
          })
        }
        if (!parent.expanded) {
          // Expand it, wait for the load to land, then select everything.
          const onceLoaded = () => {
            this.gridApi.removeEventListener('modelUpdated', onceLoaded)
            selectLoadedChildren()
          }
          this.gridApi.addEventListener('modelUpdated', onceLoaded)
          parent.setExpanded(true)
        } else {
          // Already expanded — with cacheBlockSize covering the whole group,
          selectLoadedChildren()
        }
      },
      handleSelected () {
        setTimeout(() => {
          this.selection = this.gridApi.getSelectedRows()
        }, 10)
      }
    },
    watch: {
      hideDecimals (newValue) {
        this.gridApi.redrawRows()
      },
      'selectedCodes.transaction_numbers' (newValue) {
        const { ids, ranges, error } = this.parseTransactionNumbers(newValue)
        this.selectedCodes.ledger_id_in = error ? '' : ids.join(',')
        this.selectedCodes.ledger_id_btw = error ? '' : ranges.map(([from, to]) => `${from}-${to}`).join(',')
      }
    },
    beforeMount () {
      this.gridOptions = {
        // cacheBlockSize: 5000,
        singleClickEdit: false,
        undoRedoCellEditing: true,
        enterMovesDown: true,
        enterMovesDownAfterEdit: true,
        rowSelection: 'multiple',
        suppressRowClickSelection: true,
        defaultColDef: {
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          floatingFilterComponentParams: {
            suppressFilterButton: true
          },
          resizable: true,
          // sortable is per column, see buildWorksheetColumnDefs. Under SSRM the
          // sort itself is applied by the datasource, not by the grid.
          tooltipField: 'tooltip'
        },
        // Group Related
        // groupDefaultExpanded: this.displayOptions.groupingLevel,
        suppressAggFuncInHeader: false,
        groupRemoveLowestSingleChildren: true,
        pinnedBottomRowData: [],
        rowModelType: 'serverSide',
        serverSideStoreType: 'partial',   // purges collapsed/out-of-view blocks — the actual memory fix
        getServerSideStoreParams: (params) => {
          const route = params.parentRowNode ? params.parentRowNode.getRoute() : []
          const node = getNodeAtPath(this.localTree, route)
          const rowCount = node ? getDisplayRowCountForNode(node) : 100

          return {
            storeType: 'partial',
            cacheBlockSize: Math.max(100, Math.min(rowCount + 10, 20000))
          }
        },
        // treeData: true,
        // getDataPath: (data) => {
        //   let accountHierarchy = []
        //   if (data.type) {
        //     let prettyType
        //     switch (data.type.toLowerCase()) {
        //       case 'grand_total':
        //         prettyType = 'GRAND TOTAL'
        //         break

        //       default:
        //         prettyType = 'UNKNOWN EXPENSES'
        //     }
        //     accountHierarchy.push(prettyType)
        //   }
        //   if (data.grandparent) accountHierarchy.push(data.grandparent)
        //   if (data.parent) accountHierarchy.push(data.parent)
        //   if (data.code) accountHierarchy.push(data.code)

        //   return accountHierarchy
        // },
        // isServerSideGroupOpenByDefault: (params) => true,
        getRowNodeId: (data) => {
          if (data.__isGroup) {
            return 'group::' + (data.groupAccount || data.reportGroup)
          }
          return 'leaf::' + data.groupAccount + '::' + data.code
        },
        rowClassRules: {
          'ag-error': (params) => (params.data && params.data.error),
          'ag-changed': (params) => (params.data && params.data.edited),
          'ag-aggregateRow-0': (params) => (params.data && ((params.data.__isGroup && params.node.level === 0) || params.data.__isGrandTotal)),
          'ag-aggregateRow-1': (params) => (params.data && params.data.__isGroup && params.node.level === 1),
          'ag-aggregateRow-2': (params) => (params.data && params.data.__isGroup && params.node.level >= 2),
          'ag-aggregateRow-beginning': (params) =>
            !params.node.level && !(params.data && params.data.__isGroup) && params.node.data && params.node.data.account_name === 'Starting Total',
          'ag-aggregateRow-changed': (params) =>
            !params.node.level && !(params.data && params.data.__isGroup) && params.node.data && params.node.data.account_name === 'Changes This Session',
          'ag-aggregateRow-total': (params) => {
            return !params.node.level && !(params.data && params.data.__isGroup) && params.node.data && params.node.data.account_name === 'Current Total'
          }
        },
        isRowSelectable: (params) => {
          if (params.data && params.data.__isGroup) {
            return this.allowEdit
          }
          return this.isEditable(params)
        },
        onGridReady: (params) => {
          this.$data.scope.onGridReady(params)
        },
        onFirstDataRendered: (params) => {
          this.$data.scope.onFirstDataRendered(params)
        },
        onCellValueChanged: (params) => {
          this.$data.scope.onCellValueChanged(params)
        },
        onModelUpdated: (params) => {
          this.$data.scope.autoExpandLoadedGroups(params.api)
        },
        onRowGroupOpened: (params) => {
          this.$data.scope.rememberGroupExpansion(params.node)
        },
        onColumnMoved: (params) => {
          this.$data.scope.saveGridState()
        },
        onColumnResized: (params) => {
          this.$data.scope.saveGridState()
        },
        onColumnVisible: (params) => {
          this.onChangeVisibleColumns()
        },
        onFilterChanged: (params) => {
          this.$data.scope.saveFilterState()
          this.$data.scope.updateGrandTotalRow()
          // Flat mode has no groups to re-expand, and the retry loop would churn
          // the store on every keystroke in a floating filter.
          if (this.$data.scope.isGroupedByAccount) {
            this.$data.scope.forceFullExpand(params.api)
          }
        },
        onSortChanged: () => {
          // Flat mode only. The root store is the leaf level there, so ag-grid
          // re-requests it itself — nothing to do but remember the sort.
          this.$data.scope.saveGridState()
        }
      }

      this.autoGroupColumnDef = {
        headerName: 'Trans #',
        name: 'code',
        maxWidth: 200,
        minWidth: 100,
        filterValueGetter: (params) => {
          if (params.data && params.data.trans_type === 'PO') {
            return params.data.trans_number.slice(2)
          }
          if (params.data && params.data.trans_type !== 'PO') {
            return params.data.trans_number
          }
          return null
        },
        cellRendererParams: {
          suppressCount: true,
          checkbox: true
        },
        onCellClicked: (params) => this.openTransaction(params),
        // cellStyle: (params) => (!this.isEditable(params) ? { 'pointer-events': 'none' } : ''),
        cellClass: (params) => (params.data && ['PO', 'AP', 'PC', 'JE', 'PR', 'DC'].includes(params.data.trans_type) ? 'cell-blue' : ''),
        valueGetter: (params) => {
          if (params.data && params.data.__isGroup) {
            return params.data.__groupLabel
          }
          return params.data ? this.buildTransData(params.data) : params.value
        },
        valueFormatter: (params) => params.value,   // value is already fully resolved above
        // This column only exists while grouping is on, which is not sortable.
        sortable: false,
        suppressSizeToFit: true
      }

      // Mode-independent base list — bind the grid to buildWorksheetColumnDefs()
      // instead, which derives grouping/visibility/sortable from the mode.
      this.baseColumnDefs = [
        this.$data.scope.transactionTypeColumn,
        this.$data.scope.reportGroupColumn,      // NEW — hidden, drives GRAND TOTAL level
        this.$data.scope.groupAccountColumn,     // NEW — hidden, drives GROUP level
        this.$data.scope.TransactionNumberColumn, // shown only in flat mode (no auto group column then)
        this.$data.scope.accountCodeColumn,
        this.$data.scope.accountNameColumn,
        this.$data.scope.descriptionColumn,
        this.$data.scope.vendorColumn,
        this.$data.scope.seriesColumn,
        this.$data.scope.locationColumn,
        this.$data.scope.setColumn,
        this.$data.scope.freeField1Column,
        this.$data.scope.freeField2Column,
        this.$data.scope.freeField3Column,
        this.$data.scope.freeField4Column,
        this.$data.scope.insuranceColumn,
        this.$data.scope.taxCodeColumn,
        this.$data.scope.assetColumn,
        this.$data.scope.documentNumberColumn,
        this.$data.scope.dateColumn,
        this.$data.scope.poNumberColumn,
        this.$data.scope.paymentNumberColumn,
        this.$data.scope.currencyColumn,
        this.$data.scope.amountColumn
      ]
      this.columnDefs = this.buildWorksheetColumnDefs()
    },
    created () {
      this.loading = true
      this.disableReportRun = true
      this.resetFilters()
      this.getOptions()
      this.getCompanyCodes(['fetchAllCurrencies'])
        .then(() => {
          this.reportOptions.report_currency_id = this.allCodes.currencies.find((c) => c.is_base).id

          this.$store.dispatch('fetchAllVendorModels', { query: { page_size: 200 } })
            .then((response) => {
              this.vendors = sortObjectByProperty(response, 'code').map((v) => { return { display_name: v.code + ' - ' + v.name, id: v.id } })
              this.disableReportRun = false
              this.loading = false
            })
            .catch(error => {
              console.log(error)
            })
            .finally(() => {
              this.getAppSettings()
            })
          this.allowEdit = this.$store.getters.checkIfUserHasRequiredPermissions(['auth.change_gl_bible_report'])
          this.getGridStateSettings()
        })
        .catch(error => {
          console.log(error)
        })
    },
    beforeRouteLeave (to, from, next) {
      // Save grid state before leaving
      // Sending saved instance and headers as payload to be used particularly when switching the instance
      this.saveGLGridState({ instanceUrl: this.instance, headers: this.instanceHeaders })

      if ((this.jeLinesToBeUpdated.length || this.poLinesToBeUpdated.length || this.unPostedTransactionsToBeUpdated.length) && !this.$store.getters.getRouteLeaveConfirmation) {
        // Not leaving yet — unsaved changes prompt the confirmation modal, so
        // keep the data intact until the user decides.
        return this.$store.dispatch('setDestinationRoute', to)
        .then(() => this.$store.dispatch('changeModalVisibility', { entityType: 'changeLossModal', visible: true }))
        .catch(e => console.log(e))
      } else {
        // Actually leaving the page — release the large report datasets and
        // destroy the grid now, so the memory is freed even if this component
        // is kept alive (cached) and beforeDestroy never fires.
        this.clearStore()
        next()
      }
    },
    beforeDestroy () {
      this.resetComponent()
    }
  }
</script>

<style scoped>
  .gl-reporter .form-control:focus {
    box-shadow: none;
    border-color: #ced4da;
  }

  .gl-reporter .custom-select {
    background: #ffffff;
  }

  .gl-reporter .input-group .input-group-text {
    padding: 0 0.5rem 0 0;
    color: #ced4da;
    font-size: 0.875rem;
    background: #fff;
    border-left: none;
  }

  .gl-reporter .ag-theme-bootstrap .ag-root {
    border: none !important;
  }

  .gl-reporter .progress {
    height: 4px;
    opacity: 1;
    background-color: #e8e8e8;
  }

  .gl-reporter .progress-bar {
    transition: none !important;
    transition: opacity 1.5s ease;
    background-color: #a4a5bb !important;
  }

  .gl-reporter .loaded .progress-bar {
    opacity: 0;
  }

  .gl-reporter .navbar {
    height: 38px !important;
    padding: 0;
    background-color: #e8e8e8;
  }

  .nav-item > a {
    color: #444444;
    margin: 2px 0 0 5px;
    padding-left: 1em;
    padding-right: 1em;
    font-weight: 300;
    background-color: #e0e0e0;
    border-right: solid 1px #e8e8e8;
    border-left: solid 1px #e8e8e8;
  }

  .oi {
      opacity: 0.8;
      color: #444444;
      margin-right: 0.5em;
  }

  .nav-item {
      font-size: 1rem;
  }

  .nav-item > a.active {
      color: #007bff;
      font-weight: 600;
      background-color: #f5f7f7;
  }

  .nav-item.disabled {
      color: #aaa !important;
      background-color: #f0f0f0 !important;
  }
  .nav-item.disabled .oi {
      color: #aaa !important;
  }

  .nav-item.show >a {
    background-color: #ffffff;
    border-right: solid 1px #000000;
  }

  .gl-reporter .navbar .navbar-nav .nav-item > a.active {
    color: #007bff !important;
    font-weight: 700;
    background-color: #f5f7f7;
  }

  .print-options {
    background-color: aliceblue;
    z-index: 1050;
    border: 1px solid #CCCCCC;
    right: 5px;
    top: 40px;
    position: absolute;
    color: #444444;
    font-weight: normal;
    width: 150px;
  }

  .print-options div {
    margin: 10px;
    cursor: pointer;
  }

  .print-options div:hover, .print-options div:active {
    font-weight: bold;
  }

  .dropdown-menu {
    top: 37px;
    margin: 0;
    border: none;
    border-bottom: solid 1px #000000;
    border-right: solid 1px #000000;
    border-left: solid 1px #000000;
    border-radius: 0;
    background-color: #ffffff;
  }

  .dropdown-menu .dropdown-item.dropdown-item:hover {
      background-color: #dfdfdf;
  }

  .dropdown-menu .dropdown-divider {
    border-top: 1px solid #d8d8d8;
  }

  .nav-info {
    background-color: #5bc0de !important;
    color: #fff !important;
    font-weight: 700 !important;
    border: 1px solid #bfcbd9 !important;
    border-radius: 4px;
  }

  .nav-blue {
      background-color: #007bff !important;
      color: #fff !important;
      font-weight: 600 !important;
      border: 1px solid #bfcbd9 !important;
      border-radius: 4px;
  }

  .nav-green {
      background-color: rgb(65, 152, 72) !important;
      color: #fff !important;
      font-weight: 700 !important;
      border: 1px solid #bfcbd9 !important;
      border-radius: 4px;
  }

  .nav-red {
      background-color: #ff0000c0 !important;
      color: #fff !important;
      font-weight: 700 !important;
      border: 1px solid #bfcbd9 !important;
      border-radius: 4px;
  }

  .white {
      color: #fff !important;
  }

  .black {
      color: #000 !important;
  }

  .bold {
      font-weight: 700 !important;
  }

  .clear {
      clear: both;
  }
  .small { font-size: 8px; }
  .right { float: right; text-align: right; padding: 0px 5px; }
  .left { float: left; text-align: left; padding: 0px 5px; }
  .selectRow { padding: 0px 20px; }
  .section { padding: 40px 20px 0px;  }
  .headerRow { padding: 15px 20px 0px; }
  .m-top-10 { margin-top: 10px; }
  .m-top-20 { margin-top: 20px; }
  .m-right-20 { margin-right: 20px; }
</style>

<style>
  .gl-reporter .ag-theme-balham .ag-cell.cell-invalid {
    background-color: #ffdada !important;
    border: 1px solid #d32f2f !important;
  }
  .gl-reporter .el-select { display: block; }
  .el-transfer-panel { width:36%; max-width: 350px }
  .el-transfer-panel__body { min-height: 400px }
  .el-transfer-panel__list { min-height: 360px }
  .gl-reporter .ag-theme-balham .ag-row.ag-aggregateRow-0 {
    background-color: #67cbf8;
  }

  .gl-reporter .ag-theme-balham .ag-row.ag-aggregateRow-1 {
    background-color: #b3e5fc;
  }

  .gl-reporter .ag-theme-balham .ag-row.ag-aggregateRow-2 {
    background-color: #e1f5fe;
  }
  .gl-reporter .ag-theme-balham .ag-ltr .ag-cell.cell-blue {
    color: #007bff !important;
    cursor: pointer !important;
  }
  .gl-reporter .navbar-expand .navbar-nav .nav-link {
    padding-right: 1rem;
    padding-left: 1rem;
  }
  .gl-reporter .navbar-light .navbar-nav .nav-link.active.print {
    color: white !important;
  }
  .gl-reporter .navbar .navbar-nav .b-nav-dropdown .dropdown-menu {
    border: none !important;
    border-bottom: 1px solid #000 !important;
    border-right: 1px solid #000 !important;
    border-left: 1px solid #000 !important;
    border-radius: 0 !important;
    background-color: #fff !important;
    min-width: 12.3rem !important;
  }
  .gl-reporter .navbar .navbar-nav .b-nav-dropdown .dropdown-menu .custom-control-label {
    min-height: 1.5rem;
  }
  .gl-reporter .bicon {
    position: relative;
    color: #444;
    width: 14px;
    height: 14px;
    right: 4px;
    bottom: 2px;
  }
  .gl-reporter .bicon.tbl {
    right: 6px;
    bottom: 4px;
    width: 12px;
    height: 12px;
  }
  .gl-reporter .bicon.save {
    right: 6px;
    width: 12px;
    height: 12px;
    bottom: 3px;
  }
  .gl-reporter .acc { color: #007bff; text-decoration: underline; }
</style>



// WEBPACK FOOTER //
// src/components/Reports/GlReportCustom.vue